<template>
  <page-header-wrapper>
    <div class="dashboard-container">
      <!-- 顶部项目数据 -->
      <a-card :bordered="false" class="section-card project-summary-card">
        <div class="project-summary-header">
          <div class="project-summary-header-left">
            <span class="project-summary-title">项目数据</span>
           
          </div>
          <div class="project-summary-header-right">
            <span class="time-filter-label">时间</span>
            <a-range-picker
              v-model:value="dateRange"
              format="YYYY-MM-DD"
              valueFormat="YYYY-MM-DD"
              :placeholder="['开始时间', '结束时间']"
              :disabled-date="disableFutureDate"
              inputReadOnly
              allow-clear
              class="time-filter-picker"
              @change="handleDateChange"
            />
          </div>
        </div>
        <div class="project-stat">
          <div class="project-circle">
            <div ref="usageChart" class="usage-chart"></div>
            <div class="circle-text">
              <strong>{{ projectSummary.usage }}%</strong>
              <span>启用率</span>
            </div>
          </div>
          <div class="project-summary-list">
            <div
              class="summary-card"
              v-for="item in projectSummary.items"
              :key="item.label"
            >
              <div class="summary-icon">
                <img :src="getIconImage(item.label)" :alt="item.label" />
              </div>
              <div class="summary-info" style="margin-left:16px">
                <p class="label">{{ item.label }}</p>
                <p class="value">{{ item.value }}</p>
              </div>
            </div>
          </div>
        </div>
      </a-card>

      <!-- 任务/手环数据 -->
      <a-row :gutter="[16, 16]">
        <a-col :xs="24" :sm="24" :md="24" :lg="14" :xl="14" :xxl="14">
          <a-card title="任务数据" :bordered="false" class="section-card">
            <div class="table-wrapper">
              <a-table
                :columns="taskColumns"
                :data-source="taskData"
                :pagination="false"
                row-key="id"
                :customRow="getTaskTableRowProps"
                :scroll="{ x: 'max-content' }"
              >
                <template #taskProjectName="{ text, record }">
                  <a class="task-project-link" @click.stop="openTaskDetail(record)">{{ text }}</a>
                </template>
              </a-table>
            </div>
          </a-card>
        </a-col>
        <a-col :xs="24" :sm="24" :md="24" :lg="10" :xl="10" :xxl="10">
          <a-card title="手环数据" :bordered="false" class="section-card">
            <div class="table-wrapper">
              <a-table
                :columns="wristbandColumns"
                :data-source="wristbandData"
                :pagination="false"
                row-key="name"
                :customRow="getWristbandTableRowProps"
                :scroll="{ x: 'max-content' }"
              />
            </div>
          </a-card>
        </a-col>
      </a-row>

      <!-- 投诉/排行榜 -->
      <a-row :gutter="[16, 16]">
        <a-col :xs="24" :sm="24" :md="24" :lg="16" :xl="16" :xxl="16">
          <a-card title="投诉数据" :bordered="false" class="section-card">
            <div class="table-wrapper">
              <a-table
                :columns="complaintColumns"
                :data-source="complaintData"
                :pagination="false"
                row-key="name"
                :customRow="getComplaintTableRowProps"
                :scroll="{ x: 'max-content' }"
              />
            </div>
          </a-card>
        </a-col>
        <a-col :xs="24" :sm="24" :md="24" :lg="8" :xl="8" :xxl="8">
          <a-card title="空间信标数据" :bordered="false" class="section-card">
            <div class="table-wrapper">
              <a-table
                :columns="rankColumns"
                :data-source="rankData"
                :pagination="false"
                row-key="name"
                :scroll="{ x: 'max-content' }"
              />
            </div>
          </a-card>
        </a-col>
      </a-row>

      <!-- 出勤/健康/今日步数 -->
      <a-row :gutter="[16, 16]" class="bottom-section">
        <a-col :xs="24" :sm="24" :md="24" :lg="9" :xl="9" :xxl="9">
          <a-card title="出勤数据" :bordered="false" class="section-card">
            <div class="scrollable">
              <a-table
                :columns="attendanceColumns"
                :data-source="attendanceData"
                :pagination="false"
                size="small"
                row-key="idx"
                :customRow="getAttendanceTableRowProps"
                :scroll="{ x: 'max-content' }"
              />
            </div>
          </a-card>
        </a-col>
        <a-col :xs="24" :sm="24" :md="24" :lg="9" :xl="9" :xxl="9">
          <a-card title="健康数据" :bordered="false" class="section-card">
            <div class="scrollable">
              <a-table
                :columns="healthColumns"
                :data-source="healthData"
                :pagination="false"
                size="small"
                row-key="idx"
                :customRow="getHealthTableRowProps"
                :scroll="{ x: 'max-content' }"
              >
                <template #healthWarning="{ record }">
                  <a
                    v-if="resolveProjectId(record) || record.name"
                    class="task-project-link"
                    @click.stop="openHealthAlertDetail(record)"
                  >
                    {{ record.warning }}
                  </a>
                  <span v-else>{{ record.warning }}</span>
                </template>
              </a-table>
            </div>
          </a-card>
        </a-col>
        <a-col :xs="24" :sm="24" :md="24" :lg="6" :xl="6" :xxl="6">
          <a-card :bordered="false" class="section-card step-card">
            <div class="step-card__header">
              <span>今日步数排行</span>
              <small class="update-time">{{ stepRank.updateTime }} 更新</small>
            </div>
            <div class="scrollable step-rank-panel-body">
              <template v-if="stepRank.items && stepRank.items.length">
                <ul class="rank-list">
                  <li v-for="item in stepRank.items" :key="item.rank">
                    <span class="rank-num" :class="'rk-' + item.rank">{{
                      item.rank
                    }}</span>
                    <img
                      :src="item.avatar || defaultStepAvatar"
                      alt="avatar"
                      @error="onStepAvatarError"
                    />
                    <div class="rank-info">
                      <p class="name">{{ item.name }}</p>
                      <p class="desc">{{ item.project }}</p>
                    </div>
                    <span class="rank-step">{{ item.steps }}步</span>
                  </li>
                </ul>
              </template>
              <a-empty v-else description="暂无数据" class="panel-empty" />
            </div>
          </a-card>
        </a-col>
      </a-row>

      <!-- 任务详情弹窗 -->
      <a-modal
        :visible="taskDetailVisible"
        :footer="null"
        width="1100px"
        title="任务详情"
        @cancel="handleTaskDetailClose"
      >
        <div class="task-detail-header">
          <div class="task-detail-header-row">
            <a-button-group class="filter-buttons">
              <a-button
                :type="taskDetailFilterType === 'today' ? 'primary' : 'default'"
                @click="handleTaskDetailFilterChange('today')"
                >今日</a-button
              >
              <a-button
                :type="taskDetailFilterType === 'week' ? 'primary' : 'default'"
                @click="handleTaskDetailFilterChange('week')"
                >本周</a-button
              >
              <a-button
                :type="taskDetailFilterType === 'month' ? 'primary' : 'default'"
                @click="handleTaskDetailFilterChange('month')"
                >本月</a-button
              >
            </a-button-group>
            <div class="task-detail-header-right">
              <a-radio-group
                v-model:value="taskDetailFilterType"
                button-style="solid"
                @change="handleTaskDetailFilterChange('custom')"
                class="custom-date-radio"
              >
                <a-radio-button value="custom">自定义</a-radio-button>
              </a-radio-group>
              <a-range-picker
                v-model:value="taskDetailDateRange"
                :disabled-date="disableFutureDate"
                format="YYYY-MM-DD"
                valueFormat="YYYY-MM-DD"
                :placeholder="['开始时间', '结束时间']"
                inputReadOnly
                allow-clear
                class="task-detail-date-picker"
                @change="handleTaskDetailDateRangeChange"
              />
            </div>
          </div>
          <div class="task-detail-header-row task-detail-status-row">
            <span class="task-detail-filter-label">当前进度</span>
            <a-select
              v-model:value="taskDetailStatusFilter"
              placeholder="全部"
              allow-clear
              class="task-detail-status-select"
              style="width: 120px; border-radius: 4px"
              @change="handleTaskDetailStatusFilterChange"
            >
              <a-select-option value="">全部</a-select-option>
              <a-select-option :value="3">待定</a-select-option>
              <a-select-option :value="0">未开始</a-select-option>
              <a-select-option :value="1">进行中</a-select-option>
              <a-select-option :value="2">已完成</a-select-option>
            </a-select>
            <span class="task-detail-filter-label">人员</span>
            <a-select
              v-model:value="taskDetailUserIds"
              mode="multiple"
              allow-clear
              show-search
              option-filter-prop="title"
              :max-tag-count="2"
              placeholder="人员 / 完成率"
              class="task-detail-user-select"
              @change="handleTaskDetailUserFilterChange"
            >
              <a-select-option
                v-for="u in taskDetailUserOptions"
                :key="u.userId"
                :value="u.userId"
                :title="`${u.nickname} ${formatTaskCompletionRate(u.completionRate)}`"
              >
                {{ u.nickname }} {{ formatTaskCompletionRate(u.completionRate) }}
              </a-select-option>
            </a-select>
            <a-button
              type="primary"
              ghost
              :loading="taskDetailExportLoading"
              @click="handleTaskDetailExport"
            >导出</a-button>
          </div>
        </div>
        <template v-if="taskDetailVisible">
          <a-table
            :key="'task-detail-' + taskDetailPageNum + '-' + taskDetailTotal"
            :columns="taskDetailColumns"
            :data-source="taskDetailData"
            :loading="taskDetailLoading"
            :pagination="false"
            :row-key="getTaskDetailRowKey"
            :scroll="{ x: 'max-content' }"
          />
          <div class="task-detail-pagination">
            <a-pagination
              :current="taskDetailPageNum"
              :page-size="taskDetailPageSize"
              :total="taskDetailTotal"
              show-size-changer
              show-quick-jumper
              @change="handleTaskDetailPageChange"
              @showSizeChange="handleTaskDetailSizeChange"
            />
          </div>
          <div class="task-detail-footer-pagination-wrapper">
            <a-pagination
              :current="taskDetailPageNum"
              :page-size="taskDetailPageSize"
              :total="taskDetailTotal"
              show-size-changer
              show-quick-jumper
              @change="handleTaskDetailPageChange"
              @showSizeChange="handleTaskDetailSizeChange"
              class="task-detail-footer-pagination"
            />
          </div>
        </template>
      </a-modal>

      <a-modal
        :visible="wristbandDetailVisible"
        :footer="null"
        width="1100px"
        title="手环详情"
        @cancel="handleWristbandDetailClose"
      >
        <div class="wristband-detail-header">
          <a-button-group class="filter-buttons">
            <a-button
              :type="wristbandDetailFilterType === 'today' ? 'primary' : 'default'"
              @click="handleWristbandDetailFilterChange('today')"
              >今日</a-button
            >
            <a-button
              :type="wristbandDetailFilterType === 'week' ? 'primary' : 'default'"
              @click="handleWristbandDetailFilterChange('week')"
              >本周</a-button
            >
            <a-button
              :type="wristbandDetailFilterType === 'month' ? 'primary' : 'default'"
              @click="handleWristbandDetailFilterChange('month')"
              >本月</a-button
            >
          </a-button-group>
          <div class="wristband-detail-header-right">
            <a-radio-group
              v-model:value="wristbandDetailFilterType"
              button-style="solid"
              @change="handleWristbandDetailFilterChange('custom')"
              class="custom-date-radio"
            >
              <a-radio-button value="custom">自定义</a-radio-button>
            </a-radio-group>
            <a-range-picker
              v-model:value="wristbandDetailDateRange"
              :disabled-date="disableFutureDate"
              format="YYYY-MM-DD"
              valueFormat="YYYY-MM-DD"
              :placeholder="['开始时间', '结束时间']"
              inputReadOnly
              allow-clear
              class="task-detail-date-picker"
              @change="handleWristbandDetailDateRangeChange"
            />
           
          </div>
        </div>
        <a-table
          :columns="wristbandDetailColumns"
          :data-source="wristbandDetailData"
          :loading="wristbandDetailLoading"
          :pagination="false"
          :row-key="getWristbandDetailRowKey"
          :scroll="{ x: 'max-content' }"
        />
        <div class="wristband-detail-footer">
          <!-- <div class="wristband-detail-footer-left">
          </div> -->
          <div class="task-detail-footer-pagination-wrapper">
            <a-pagination
              :current="wristbandDetailPageNum"
              :page-size="wristbandDetailPageSize"
              :total="wristbandDetailTotal"
              show-size-changer
              show-quick-jumper
              @change="handleWristbandDetailPageChange"
              @showSizeChange="handleWristbandDetailSizeChange"
              class="task-detail-footer-pagination"
            />
          </div>
        </div>
      </a-modal>

      <a-modal
        :visible="attendanceDetailVisible"
        :footer="null"
        width="1100px"
        title="出勤详情"
        @cancel="handleAttendanceDetailClose"
      >
        <div class="wristband-detail-header">
          <a-button-group class="filter-buttons">
            <a-button
              :type="attendanceDetailFilterType === 'today' ? 'primary' : 'default'"
              @click="handleAttendanceDetailFilterChange('today')"
              >今日</a-button
            >
            <a-button
              :type="attendanceDetailFilterType === 'week' ? 'primary' : 'default'"
              @click="handleAttendanceDetailFilterChange('week')"
              >本周</a-button
            >
            <a-button
              :type="attendanceDetailFilterType === 'month' ? 'primary' : 'default'"
              @click="handleAttendanceDetailFilterChange('month')"
              >本月</a-button
            >
          </a-button-group>
          <div class="wristband-detail-header-right">
            <a-radio-group
              v-model:value="attendanceDetailFilterType"
              button-style="solid"
              @change="handleAttendanceDetailFilterChange('custom')"
              class="custom-date-radio"
            >
              <a-radio-button value="custom">自定义</a-radio-button>
            </a-radio-group>
            <a-range-picker
              v-model:value="attendanceDetailDateRange"
              :disabled-date="disableFutureDate"
              format="YYYY-MM-DD"
              valueFormat="YYYY-MM-DD"
              :placeholder="['开始时间', '结束时间']"
              inputReadOnly
              allow-clear
              class="task-detail-date-picker"
              @change="handleAttendanceDetailDateRangeChange"
            />
           
          </div>
        </div>
        <a-table
          :columns="attendanceDetailColumns"
          :data-source="attendanceDetailData"
          :loading="attendanceDetailLoading"
          :pagination="false"
          :row-key="getAttendanceDetailRowKey"
          :scroll="{ x: 'max-content' }"
        />
        <div class="wristband-detail-footer">
          <div class="wristband-detail-footer-left" style="display: none;">
            第{{ attendanceDetailData.length ? (attendanceDetailPageNum - 1) * attendanceDetailPageSize + 1 : 0 }}-{{ Math.min(attendanceDetailPageNum * attendanceDetailPageSize, attendanceDetailTotal) }}条数据/共{{ attendanceDetailTotal }}条
          </div>
          <div class="task-detail-footer-pagination-wrapper">
            <a-pagination
              :current="attendanceDetailPageNum"
              :page-size="attendanceDetailPageSize"
              :total="attendanceDetailTotal"
              show-size-changer
              show-quick-jumper
              @change="handleAttendanceDetailPageChange"
              @showSizeChange="handleAttendanceDetailSizeChange"
              class="task-detail-footer-pagination"
            />
          </div>
        </div>
      </a-modal>

      <a-modal
        :visible="complaintDetailVisible"
        :footer="null"
        width="1100px"
        title="投诉详情"
        @cancel="handleComplaintDetailClose"
      >
        <div class="task-detail-header complaint-detail-header">
          <a-button-group class="filter-buttons">
            <a-button
              :type="complaintDetailFilterType === 'today' ? 'primary' : 'default'"
              @click="handleComplaintDetailFilterChange('today')"
              >今日</a-button
            >
            <a-button
              :type="complaintDetailFilterType === 'week' ? 'primary' : 'default'"
              @click="handleComplaintDetailFilterChange('week')"
              >本周</a-button
            >
            <a-button
              :type="complaintDetailFilterType === 'month' ? 'primary' : 'default'"
              @click="handleComplaintDetailFilterChange('month')"
              >本月</a-button
            >
          </a-button-group>
          <div class="task-detail-header-right">
            <a-radio-group
              v-model:value="complaintDetailFilterType"
              button-style="solid"
              @change="handleComplaintDetailFilterChange('custom')"
              class="custom-date-radio"
            >
              <a-radio-button value="custom">自定义</a-radio-button>
            </a-radio-group>
            <a-range-picker
              v-model:value="complaintDetailDateRange"
              :disabled-date="disableFutureDate"
              format="YYYY-MM-DD"
              valueFormat="YYYY-MM-DD"
              :placeholder="['开始时间', '结束时间']"
              inputReadOnly
              allow-clear
              class="task-detail-date-picker"
              @change="handleComplaintDetailDateRangeChange"
            />
          </div>
        </div>
        <div class="task-detail-header-row task-detail-status-row">
          <span class="task-detail-filter-label">类型</span>
          <a-select
            v-model:value="complaintDetailTypeFilter"
            allow-clear
            placeholder="全部类型"
            style="width: 140px"
            @change="handleComplaintDetailExtraFilterChange"
          >
            <a-select-option value="0">投诉</a-select-option>
            <a-select-option value="1">任务需求</a-select-option>
            <a-select-option value="2">临时任务</a-select-option>
            <a-select-option value="3">巡检单</a-select-option>
          </a-select>
          <span class="task-detail-filter-label">状态</span>
          <a-select
            v-model:value="complaintDetailStatusFilter"
            allow-clear
            placeholder="全部状态"
            style="width: 120px"
            @change="handleComplaintDetailExtraFilterChange"
          >
            <a-select-option value="0">待处理</a-select-option>
            <a-select-option value="1">处理中</a-select-option>
            <a-select-option value="2">已完成</a-select-option>
          </a-select>
        </div>
        <a-table
          :columns="complaintDetailColumns"
          :data-source="complaintDetailData"
          :loading="complaintDetailLoading"
          :pagination="false"
          :row-key="getComplaintDetailRowKey"
          :scroll="{ x: 'max-content' }"
        />
        <div class="wristband-detail-footer">
          <div class="wristband-detail-footer-left" style="display: none;">
            第{{ complaintDetailData.length ? (complaintDetailPageNum - 1) * complaintDetailPageSize + 1 : 0 }}-{{ Math.min(complaintDetailPageNum * complaintDetailPageSize, complaintDetailTotal) }}条数据/共{{ complaintDetailTotal }}条
          </div>
          <div class="task-detail-footer-pagination-wrapper">
            <a-pagination
              :current="complaintDetailPageNum"
              :page-size="complaintDetailPageSize"
              :total="complaintDetailTotal"
              show-size-changer
              show-quick-jumper
              @change="handleComplaintDetailPageChange"
              @showSizeChange="handleComplaintDetailSizeChange"
              class="task-detail-footer-pagination"
            />
          </div>
        </div>
      </a-modal>

      <a-modal
        :visible="healthAlertDetailVisible"
        :footer="null"
        width="1100px"
        title="健康预警明细"
        @cancel="handleHealthAlertDetailClose"
      >
        <div class="task-detail-header health-alert-detail-header">
          <a-button-group class="filter-buttons">
            <a-button
              :type="healthAlertDetailFilterType === 'today' ? 'primary' : 'default'"
              @click="handleHealthAlertDetailFilterChange('today')"
              >今日</a-button
            >
            <a-button
              :type="healthAlertDetailFilterType === 'week' ? 'primary' : 'default'"
              @click="handleHealthAlertDetailFilterChange('week')"
              >本周</a-button
            >
            <a-button
              :type="healthAlertDetailFilterType === 'month' ? 'primary' : 'default'"
              @click="handleHealthAlertDetailFilterChange('month')"
              >本月</a-button
            >
          </a-button-group>
          <div class="task-detail-header-right">
            <a-radio-group
              v-model:value="healthAlertDetailFilterType"
              button-style="solid"
              @change="handleHealthAlertDetailFilterChange('custom')"
              class="custom-date-radio"
            >
              <a-radio-button value="custom">自定义</a-radio-button>
            </a-radio-group>
            <a-range-picker
              v-model:value="healthAlertDetailDateRange"
              :disabled-date="disableFutureDate"
              format="YYYY-MM-DD"
              valueFormat="YYYY-MM-DD"
              :placeholder="['开始时间', '结束时间']"
              inputReadOnly
              allow-clear
              class="task-detail-date-picker"
              @change="handleHealthAlertDetailDateRangeChange"
            />
          </div>
        </div>
        <a-table
          :columns="healthAlertDetailColumns"
          :data-source="healthAlertDetailData"
          :loading="healthAlertDetailLoading"
          :pagination="false"
          :row-key="getHealthAlertDetailRowKey"
          :scroll="{ x: 'max-content' }"
        />
        <div class="wristband-detail-footer">
          <div class="wristband-detail-footer-left" style="display: none;">
            第{{ healthAlertDetailData.length ? (healthAlertDetailPageNum - 1) * healthAlertDetailPageSize + 1 : 0 }}-{{ Math.min(healthAlertDetailPageNum * healthAlertDetailPageSize, healthAlertDetailTotal) }}条数据/共{{ healthAlertDetailTotal }}条
          </div>
          <div class="task-detail-footer-pagination-wrapper">
            <a-pagination
              :current="healthAlertDetailPageNum"
              :page-size="healthAlertDetailPageSize"
              :total="healthAlertDetailTotal"
              show-size-changer
              show-quick-jumper
              @change="handleHealthAlertDetailPageChange"
              @showSizeChange="handleHealthAlertDetailSizeChange"
              class="task-detail-footer-pagination"
            />
          </div>
        </div>
      </a-modal>
    </div>
  </page-header-wrapper>
</template>

<script>
import { h } from 'vue'
import {
  getCompanyDashboard,
  getTaskPage,
  getTaskUserOptions,
  getWristbandPage,
  getAttendancePage,
  getComplaintPage,
  getHealthAlertPage,
} from '@/api/system/dept'
import * as echarts from 'echarts'
import moment from 'moment'

export default {
  name: 'companyDashboard',
  components: {},
  data() {
    return {
      loading: false,
      usageChartInstance: null,
      dateRange: [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')],
      startTime: moment().format('YYYY-MM-DD'),
      endTime: moment().format('YYYY-MM-DD'),
      projectSummary: {
        usage: 0,
        items: [
          {label: '总项目数', value: 0, icon: 'book', color: '#E1F1FF'},
          {
            label: '进行中项目',
            value: 0,
            icon: 'clock-circle',
            color: '#FEE7D5',
          },
          {label: '洽谈中', value: 0, icon: 'message', color: '#E7F6F5'},
          {label: '已结束', value: 0, icon: 'poweroff', color: '#FEE2E2'},
        ],
      },
      taskColumns: [
        {title: '项目名称', dataIndex: 'name', slots: {customRender: 'taskProjectName'}},
        {title: '任务总数', dataIndex: 'total'},
        {title: '未开始', dataIndex: 'todo'},
        {title: '进行中', dataIndex: 'doing'},
        {title: '已完成', dataIndex: 'done'},
      ],
      wristbandColumns: [
        {title: '项目名称', dataIndex: 'name'},
        {title: '手环数', dataIndex: 'count'},
        {title: '今日在岗', dataIndex: 'online'},
        {title: '在岗率', dataIndex: 'rate'},
      ],
      complaintColumnsFull: [
        {title: '项目名称', dataIndex: 'name'},
        {title: '总投诉数', dataIndex: 'total'},
        {title: '今日投诉数', dataIndex: 'today'},
        {title: '处理中', dataIndex: 'processing'},
        {title: '已完成', dataIndex: 'done'},
        {title: '本月投诉数', dataIndex: 'monthTotal'},
        {title: '处理中', dataIndex: 'monthProcessing'},
        {title: '已完成', dataIndex: 'monthDone'},
      ],
      complaintColumnsShort: [
        {title: '项目名称', dataIndex: 'name'},
        {title: '总投诉数', dataIndex: 'total'},
        {title: '处理中', dataIndex: 'processing'},
        {title: '已完成', dataIndex: 'done'},
      ],
      complaintIsToday: true,
      rankColumns: [
        {title: '项目名称', dataIndex: 'name'},
        {title: '信标数', dataIndex: 'letters'},
        {title: '空间数', dataIndex: 'spaces'},
      ],
      taskData: [],
      wristbandData: [],
      complaintData: [],
      rankData: [],
      attendanceColumns: [
        {title: '项目名称', dataIndex: 'name'},
        {title: '今日应出勤', dataIndex: 'should'},
        {title: '实际出勤', dataIndex: 'actual'},
        {title: '出勤率%', dataIndex: 'rate'},
      ],
      attendanceData: [],
      healthColumns: [
        {title: '项目名称', dataIndex: 'name'},
        {title: '出勤人数', dataIndex: 'healthy'},
        {title: '健康预警次数', dataIndex: 'warning', slots: {customRender: 'healthWarning'}},
      ],
      healthData: [],
      stepRank: {
        updateTime: '',
        items: [],
      },
      // 步数排行默认头像（无头像或加载失败时使用，与 index.vue 一致）
      defaultStepAvatar:
        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23b0b0b0'%3E%3Cpath d='M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z'/%3E%3C/svg%3E",
      taskDetailVisible: false,
      taskDetailLoading: false,
      currentProjectId: undefined,
      taskDetailData: [],
      taskDetailPageNum: 1,
      taskDetailPageSize: 10,
      taskDetailTotal: 0,
      // 任务详情弹窗的筛选条件
      taskDetailFilterType: 'today', // 默认选中今日
      taskDetailDateRange: [], // 自定义时间范围
      taskDetailStatusFilter: '', // 当前进度筛选：'' 全部，3 待定，0 未开始，1 进行中，2 已完成
      taskDetailUserIds: [],
      taskDetailUserOptions: [],
      taskDetailExportLoading: false,
      taskDetailStartTime: undefined,
      taskDetailEndTime: undefined,
      taskDetailColumns: [
        {title: '序号', dataIndex: 'index'},
        {title: '空间名称', dataIndex: 'spaceName'},
        {title: '员工', dataIndex: 'nickname'},
        {title: '服务时长', dataIndex: 'serviceMinutesText'},
        {
          title: '当前进度',
          dataIndex: 'taskStatusText',
          customRender: ({ text }) => {
            if (text === '已完成') {
              return h('span', { style: { color: '#52c41a' } }, text)
            }
            return text
          },
        },
        {title: '任务时间', dataIndex: 'taskTimeText'},
      ],
      wristbandDetailVisible: false,
      wristbandDetailLoading: false,
      currentWristbandRecord: null,
      currentWristbandProjectId: undefined,
      currentWristbandProjectName: undefined,
      wristbandDetailRawData: [],
      wristbandDetailData: [],
      wristbandDetailPageNum: 1,
      wristbandDetailPageSize: 10,
      wristbandDetailTotal: 0,
      wristbandDetailFilterType: 'today',
      wristbandDetailDateRange: [],
      wristbandDetailStartTime: undefined,
      wristbandDetailEndTime: undefined,
      wristbandDetailColumns: [
        {title: '序号', dataIndex: 'index'},
        {title: '员工', dataIndex: 'employeeName'},
        {title: 'MAC码', dataIndex: 'mac'},
        {title: '心率(BPM)', dataIndex: 'heartbeat'},
        {title: '体温(℃)', dataIndex: 'temperature'},
        {
          title: '血压(mmHg)',
          key: 'bloodPressure',
          customRender: ({ record }) => {
            const s = record.systolicBp ?? '--'
            const d = record.diastolicBp ?? '--'
            return `${s}/${d}`
          },
        },
        {title: '健康更新时间', dataIndex: 'healthUpdateTime'},
      ],
      attendanceDetailVisible: false,
      attendanceDetailLoading: false,
      currentAttendanceRecord: null,
      currentAttendanceProjectId: undefined,
      currentAttendanceProjectName: undefined,
      attendanceDetailRawData: [],
      attendanceDetailData: [],
      attendanceDetailPageNum: 1,
      attendanceDetailPageSize: 10,
      attendanceDetailTotal: 0,
      attendanceDetailFilterType: 'today',
      attendanceDetailDateRange: [],
      attendanceDetailStartTime: undefined,
      attendanceDetailEndTime: undefined,
      attendanceDetailColumns: [
        {title: '序号', dataIndex: 'index'},
        {title: '员工', dataIndex: 'employeeName'},
        {title: '当前位置', dataIndex: 'currentLocation'},
        {title: '位置更新时间', dataIndex: 'locationUpdateTime'},
        {title: '工作流', dataIndex: 'hasWorkflow'},
        {title: '启动', dataIndex: 'workflowStarted'},
      ],
      complaintDetailVisible: false,
      complaintDetailLoading: false,
      currentComplaintProjectId: undefined,
      currentComplaintProjectName: undefined,
      complaintDetailRawData: [],
      complaintDetailData: [],
      complaintDetailPageNum: 1,
      complaintDetailPageSize: 10,
      complaintDetailTotal: 0,
      complaintDetailFilterType: 'today',
      complaintDetailDateRange: [],
      complaintDetailStartTime: undefined,
      complaintDetailEndTime: undefined,
      complaintDetailTypeFilter: undefined,
      complaintDetailStatusFilter: undefined,
      complaintDetailColumns: [
        {title: '序号', dataIndex: 'index'},
        {title: '空间名称', dataIndex: 'spaceName'},
        {title: '类型', dataIndex: 'typeText'},
        {title: '处理状态', dataIndex: 'complaintStatusText'},
        {title: '负责人', dataIndex: 'nickname'},
        {title: '投诉时间', dataIndex: 'complaintTimeText'},
        {title: '投诉内容', dataIndex: 'descriptionText'},
      ],
      healthAlertDetailVisible: false,
      healthAlertDetailLoading: false,
      currentHealthAlertProjectId: undefined,
      currentHealthAlertProjectName: undefined,
      healthAlertDetailData: [],
      healthAlertDetailPageNum: 1,
      healthAlertDetailPageSize: 10,
      healthAlertDetailTotal: 0,
      healthAlertDetailFilterType: 'today',
      healthAlertDetailDateRange: [],
      healthAlertDetailStartTime: undefined,
      healthAlertDetailEndTime: undefined,
      healthAlertDetailColumns: [
        {title: '序号', dataIndex: 'index'},
        {title: '员工', dataIndex: 'employeeName'},
        {title: '预警数据', dataIndex: 'metricData'},
        {title: '预警类型', dataIndex: 'metricName'},
        {title: '预警时间', dataIndex: 'uploadTime'},
      ],
    }
  },
  created() {
    this.getDashboardData()
  },
  mounted() {
    this.initUsageChart()
  },
  activated() {
    this.getDashboardData()
    this.$nextTick(() => {
      this.initUsageChart()
    })
  },
      beforeUnmount() {
        if (this.usageChartInstance) {
          window.removeEventListener('resize', this.handleUsageChartResize)
          this.usageChartInstance.dispose()
          this.usageChartInstance = null
        }
      },
      computed: {
        complaintColumns() {
          return this.complaintIsToday ? this.complaintColumnsFull : this.complaintColumnsShort
        },
      },
      watch: {
        'projectSummary.usage'() {
          this.updateUsageChart()
        },
        wristbandDetailVisible(val) {
          if (val && (this.currentWristbandProjectId || this.currentWristbandProjectName)) {
            this.$nextTick(() => this.fetchWristbandDetail())
          }
        },
        complaintDetailVisible(val) {
          if (val && (this.currentComplaintProjectId || this.currentComplaintProjectName)) {
            this.$nextTick(() => this.fetchComplaintDetail())
          }
        },
        attendanceDetailVisible(val) {
          if (val && (this.currentAttendanceProjectId || this.currentAttendanceProjectName)) {
            this.$nextTick(() => this.fetchAttendanceDetail())
          }
        },
        healthAlertDetailVisible(val) {
          if (val && (this.currentHealthAlertProjectId || this.currentHealthAlertProjectName)) {
            this.$nextTick(() => this.fetchHealthAlertDetail())
          }
        },
      },
      methods: {
        // 根据标签名称返回对应的图片路径
        getIconImage(label) {
          const imageMap = {
            '总项目数': require('@/assets/images/dashbord/1.png'),
            '进行中项目': require('@/assets/images/dashbord/2.png'),
            '洽谈中': require('@/assets/images/dashbord/3.png'),
            '已结束': require('@/assets/images/dashbord/4.png'),
          }
          return imageMap[label] || ''
        },
        initUsageChart() {
          if (!this.$refs.usageChart) {
            return
          }
          if (!this.usageChartInstance) {
            this.usageChartInstance = echarts.init(this.$refs.usageChart)
            window.addEventListener('resize', this.handleUsageChartResize)
          }
          this.updateUsageChart()
        },
        handleUsageChartResize() {
          if (this.usageChartInstance) {
            this.usageChartInstance.resize()
          }
        },
        updateUsageChart() {
          if (!this.usageChartInstance) {
            return
          }
          const percent = Number(this.projectSummary.usage) || 0
          const option = {
            tooltip: {show: false},
            series: [
              {
                type: 'pie',
                radius: ['68%', '88%'],
                silent: true,
                label: {show: false},
                data: [
                  {value: percent, itemStyle: {color: '#165DFF'}},
                  {
                    value: Math.max(0, 100 - percent),
                    itemStyle: {color: '#ecf1ff'},
                  },
                ],
              },
            ],
          }
          this.usageChartInstance.setOption(option)
        },
        /**
         * 时间筛选变化
         */
        handleDateChange(dates, dateStrings) {
          if (!dates || dates.length === 0) {
            this.startTime = undefined
            this.endTime = undefined
          } else {
            this.startTime = dateStrings[0] || undefined
            this.endTime = dateStrings[1] || undefined
          }
          this.getDashboardData()
        },
        disableFutureDate(current) {
          if (!current) {
            return false
          }
          const currentTime =
            typeof current.valueOf === 'function'
              ? current.valueOf()
              : moment(current, 'YYYY-MM-DD').valueOf()
          return currentTime > moment().endOf('day').valueOf()
        },
        getTaskTableRowProps(record) {
          const handleClick = () => this.openTaskDetail(record)
          return {
            style: { cursor: 'pointer' },
            onClick: handleClick,
            on: {
              click: handleClick,
            },
          }
        },
        getWristbandTableRowProps(record) {
          const handleClick = () => this.openWristbandDetail(record)
          return {
            style: { cursor: 'pointer' },
            onClick: handleClick,
            on: {
              click: handleClick,
            },
          }
        },
        getAttendanceTableRowProps(record) {
          const handleClick = () => this.openAttendanceDetail(record)
          return {
            style: { cursor: 'pointer' },
            onClick: handleClick,
            on: {
              click: handleClick,
            },
          }
        },
        getComplaintTableRowProps(record) {
          const handleClick = () => this.openComplaintDetail(record)
          return {
            style: { cursor: 'pointer' },
            onClick: handleClick,
            on: {
              click: handleClick,
            },
          }
        },
        getHealthTableRowProps(record) {
          const clickable = Boolean(this.resolveProjectId(record) || record?.name)
          const handleClick = () => {
            if (clickable) {
              this.openHealthAlertDetail(record)
            }
          }
          return {
            style: { cursor: clickable ? 'pointer' : 'default' },
            onClick: handleClick,
            on: {
              click: handleClick,
            },
          }
        },
        resolveProjectId(record) {
          if (!record) {
            return undefined
          }

          const directProjectId =
            record.projectId ||
            record.project_id ||
            record.id ||
            record.deptId ||
            record.dept_id ||
            record.departmentId ||
            record.orgId
          if (directProjectId) {
            return directProjectId
          }

          const projectName = record.name || record.projectName
          if (!projectName) {
            return undefined
          }

          const datasets = [
            this.taskData,
            this.wristbandData,
            this.complaintData,
            this.attendanceData,
            this.healthData,
            this.rankData,
          ]

          for (const dataset of datasets) {
            const matched = (dataset || []).find((item) => {
              return (item.name || item.projectName) === projectName
            })
            if (matched) {
              const matchedProjectId =
                matched.projectId ||
                matched.project_id ||
                matched.id ||
                matched.deptId ||
                matched.dept_id ||
                matched.departmentId ||
                matched.orgId
              if (matchedProjectId) {
                return matchedProjectId
              }
            }
          }

          return undefined
        },
        // 任务详情弹窗的筛选类型切换 (今日/本周/本月/自定义)，与 index.vue 一致：日期区间展示在日期组件并请求表格数据
        handleTaskDetailFilterChange(type) {
          this.taskDetailFilterType = type
          const today = moment().format('YYYY-MM-DD')
          let start, end
          switch (type) {
            case 'today':
              start = today
              end = today
              this.taskDetailDateRange = [start, end]
              break
            case 'week':
              start = moment().startOf('week').format('YYYY-MM-DD')
              end = moment().endOf('week').format('YYYY-MM-DD')
              this.taskDetailDateRange = [start, end]
              break
            case 'month':
              start = moment().startOf('month').format('YYYY-MM-DD')
              end = moment().endOf('month').format('YYYY-MM-DD')
              this.taskDetailDateRange = [start, end]
              break
            case 'custom':
              if (this.taskDetailDateRange && this.taskDetailDateRange.length === 2) {
                this.taskDetailStartTime = this.normalizeDateValue(this.taskDetailDateRange[0])
                this.taskDetailEndTime = this.normalizeDateValue(this.taskDetailDateRange[1])
              } else {
                this.taskDetailStartTime = undefined
                this.taskDetailEndTime = undefined
              }
              this.taskDetailPageNum = 1
              this.refreshTaskDetailUserFilter()
              this.fetchTaskDetail()
              return
          }
          this.taskDetailStartTime = start
          this.taskDetailEndTime = end
          this.taskDetailPageNum = 1
          this.refreshTaskDetailUserFilter()
          this.fetchTaskDetail()
        },
        // 任务详情弹窗的日期区间变化
        handleTaskDetailDateRangeChange(dates, dateStrings) {
          if (!dates || dates.length === 0) {
            this.taskDetailStartTime = undefined
            this.taskDetailEndTime = undefined
          } else {
            this.taskDetailStartTime = dateStrings[0] || undefined
            this.taskDetailEndTime = dateStrings[1] || undefined
          }
          // 确保在自定义模式下才触发重新加载数据，其他模式由 handleTaskDetailFilterChange 处理
          if (this.taskDetailFilterType === 'custom') {
            this.refreshTaskDetailUserFilter()
            this.fetchTaskDetail()
          }
        },
        /**
         * 获取仪表盘数据
         */
        getDashboardData() {
          this.loading = true
          const params = {
            startTime: this.startTime,
            endTime: this.endTime,
          }
          getCompanyDashboard(params)
            .then((response) => {
              if (response && response.data) {
                const data = response.data
                // 更新项目汇总数据
                if (data.projectSummary) {
                  this.projectSummary = {
                    usage: data.projectSummary.usage,
                    items: data.projectSummary.items || [],
                  }
                }
                // 更新任务数据
                if (data.taskData) {
                  this.taskData = data.taskData
                }
                // 更新手环数据
                if (data.wristbandData) {
                  this.wristbandData = data.wristbandData
                }
                // 更新投诉数据
                if (data.complaintData) {
                  this.complaintData = data.complaintData
                }
                this.complaintIsToday = data.isToday === true
                // 更新排行榜数据
                if (data.rankData) {
                  this.rankData = data.rankData
                }
                // 更新出勤数据
                if (data.attendanceData) {
                  this.attendanceData = data.attendanceData
                }
                // 更新健康数据
                if (data.healthData) {
                  this.healthData = data.healthData
                }
                // 更新步数排行数据
                if (data.stepRank) {
                  this.stepRank = {
                    updateTime: data.stepRank.updateTime,
                    items: data.stepRank.items || [],
                  }
                }
              }
            })
            .catch(() => {
              this.$message.error('获取仪表盘数据失败', 3)
            })
            .finally(() => {
              this.updateUsageChart()
              this.loading = false
            })
        },
        /** 今日步数排行：头像加载失败时使用默认头像（与 index.vue 一致） */
        onStepAvatarError(e) {
          if (e.target.src !== this.defaultStepAvatar) {
            e.target.src = this.defaultStepAvatar
          }
        },
        // 打开任务详情弹窗（点击“任务详情”按钮）
        openTaskDetail(record) {
          if (!record) {
            return
          }
          const projectId = this.resolveProjectId(record)
          if (!projectId) {
            // 某些汇总行可能没有项目ID，允许查看“公司级”任务详情
            this.currentProjectId = undefined
          } else {
            this.currentProjectId = projectId
          }
          this.taskDetailPageNum = 1
          this.taskDetailPageSize = 10
          this.taskDetailVisible = true
          if (this.startTime && this.endTime) {
            this.taskDetailFilterType = 'custom'
            this.taskDetailDateRange = [this.startTime, this.endTime]
            this.taskDetailStartTime = this.startTime
            this.taskDetailEndTime = this.endTime
          } else {
            // 默认选中今日（安全获取，避免未注入 moment 导致后续 fetchTaskDetail 未执行）
            const today = (this.$moment && this.$moment().format('YYYY-MM-DD')) || (typeof moment !== 'undefined' && moment().format('YYYY-MM-DD')) || ''
            this.taskDetailFilterType = 'today'
            this.taskDetailDateRange = today ? [today, today] : []
            this.taskDetailStartTime = today || undefined
            this.taskDetailEndTime = today || undefined
          }
          this.taskDetailUserIds = []
          this.fetchTaskDetailUserOptions()
          // 等弹窗和表格挂载后再请求，避免首屏数据不展示
          this.$nextTick(() => {
            this.fetchTaskDetail()
          })
        },
        // 拉取任务详情数据
        fetchTaskDetail() {
          this.taskDetailLoading = true
          const params = {
            pageNum: this.taskDetailPageNum,
            pageSize: this.taskDetailPageSize,
          }
          // 只有当存在项目ID时才按项目过滤，缺少项目ID则查询公司级汇总数据
          if (this.currentProjectId) {
            params.projectId = this.currentProjectId
          }
          // 检查弹窗内部的时间筛选条件
          if (this.taskDetailStartTime) {
            params.startTime = this.taskDetailStartTime
          }
          if (this.taskDetailEndTime) {
            params.endTime = this.taskDetailEndTime
          }
          // 当前进度筛选：后端可能用 taskStatus 或 status，传字符串兼容性更好
          const statusVal = this.taskDetailStatusFilter
          if (statusVal !== '' && statusVal !== undefined && statusVal !== null) {
            const v = String(statusVal)
            params.taskStatus = v
            params.status = v
          }
          if (this.taskDetailUserIds && this.taskDetailUserIds.length > 0) {
            params.userIds = this.taskDetailUserIds
          }
          getTaskPage(params)
            .then((res) => {
              const page = res?.data || {}
              let records = page.records || []
              const statusVal = this.taskDetailStatusFilter
              const hasStatusFilter = statusVal !== '' && statusVal !== undefined && statusVal !== null
              // 若后端未按状态过滤，则前端过滤当前页（保证筛选有可见效果）
              if (hasStatusFilter) {
                const want = statusVal
                records = records.filter((item) => {
                  const s = item.taskStatus ?? item.status
                  return s == want || Number(s) === Number(want)
                })
              }
              const startIndex = (this.taskDetailPageNum - 1) * this.taskDetailPageSize
              this.taskDetailTotal = hasStatusFilter ? records.length : (page.total || 0)
              this.taskDetailData = records.map((item, index) => ({
                index: startIndex + index + 1,
                taskNumber: item.taskNumber ?? item.taskName ?? '--',
                spaceName: item.spaceName || item.taskName || '--',
                nickname: item.nickname || item.employeeName || '--',
                serviceMinutesText:
                  item.serviceMinutes !== undefined && item.serviceMinutes !== null
                    ? `${item.serviceMinutes}`
                    : item.duration || '--',
                taskStatusText: this.formatTaskStatus(item.taskStatus ?? item.status),
                taskTimeText:
                  item.taskTime ||
                  item.serviceTime ||
                  item.startTime ||
                  item.createTime ||
                  '--',
                ...item,
              }))
            })
            .catch(() => {
              this.$message.error('获取任务详情失败')
            })
            .finally(() => {
              this.taskDetailLoading = false
            })
        },
        handleTaskDetailPageChange(page, pageSize) {
          this.taskDetailPageNum = page
          this.taskDetailPageSize = pageSize
          this.fetchTaskDetail()
        },
        handleTaskDetailStatusFilterChange() {
          this.taskDetailPageNum = 1
          this.fetchTaskDetail()
        },
        handleTaskDetailUserFilterChange() {
          this.taskDetailPageNum = 1
          this.fetchTaskDetail()
        },
        formatTaskCompletionRate(rate) {
          const n = rate == null || rate === '' ? 0 : Number(rate)
          return `${Number.isNaN(n) ? 0 : n}%`
        },
        refreshTaskDetailUserFilter() {
          this.taskDetailUserIds = []
          this.fetchTaskDetailUserOptions()
        },
        fetchTaskDetailUserOptions() {
          if (!this.currentProjectId) {
            this.taskDetailUserOptions = []
            return
          }
          const params = { projectId: this.currentProjectId }
          if (this.taskDetailStartTime) {
            params.startTime = this.taskDetailStartTime
          }
          if (this.taskDetailEndTime) {
            params.endTime = this.taskDetailEndTime
          }
          getTaskUserOptions(params)
            .then((res) => {
              this.taskDetailUserOptions = (res && res.data) ? res.data : []
            })
            .catch(() => {
              this.taskDetailUserOptions = []
            })
        },
        handleTaskDetailExport() {
          if (!this.currentProjectId) {
            this.$message.warning('请先选择项目')
            return
          }
          const that = this
          this.$confirm({
            title: '是否确认导出?',
            content: '将导出当前筛选条件下的全部任务详情',
            onOk() {
              that.taskDetailExportLoading = true
              const params = { projectId: that.currentProjectId }
              if (that.taskDetailStartTime) params.startTime = that.taskDetailStartTime
              if (that.taskDetailEndTime) params.endTime = that.taskDetailEndTime
              const statusVal = that.taskDetailStatusFilter
              if (statusVal !== '' && statusVal !== undefined && statusVal !== null) {
                params.taskStatus = statusVal
              }
              if (that.taskDetailUserIds && that.taskDetailUserIds.length > 0) {
                params.userIds = that.taskDetailUserIds
              }
              return that.download(
                'system/dept/taskExport',
                params,
                `任务详情_${new Date().getTime()}.xlsx`
              ).finally(() => {
                that.taskDetailExportLoading = false
              })
            },
          })
        },
        normalizeDateValue(value) {
          if (!value) {
            return undefined
          }
          if (typeof value === 'string') {
            return value
          }
          if (value.format) {
            return value.format('YYYY-MM-DD')
          }
          return undefined
        },
        formatTaskStatus(status) {
          const statusMap = {
            3: '待定',
            0: '未开始',
            1: '进行中',
            2: '已完成',
          }
          return statusMap[status] || statusMap[String(status)] || status || '--'
        },
        getTaskDetailRowKey(record, index) {
          return (
            record.id ||
            `${record.taskNumber || index}-${record.spaceName || ''}-${record.nickname || ''}-${index}`
          )
        },
        handleTaskDetailSizeChange(current, size) {
          this.taskDetailPageNum = 1
          this.taskDetailPageSize = size
          this.fetchTaskDetail()
        },
        handleTaskDetailClose() {
          this.taskDetailVisible = false
          // 关闭弹窗时重置所有筛选和分页状态
          this.currentProjectId = undefined
          this.taskDetailData = []
          this.taskDetailPageNum = 1
          this.taskDetailPageSize = 10
          this.taskDetailTotal = 0
          this.taskDetailFilterType = 'today'
          this.taskDetailDateRange = []
          this.taskDetailStartTime = undefined
          this.taskDetailEndTime = undefined
          this.taskDetailStatusFilter = ''
          this.taskDetailUserIds = []
          this.taskDetailUserOptions = []
          this.taskDetailExportLoading = false
        },
        openWristbandDetail(record) {
          if (!record) {
            return
          }
          const projectId = this.resolveProjectId(record)
          const projectName = record.name || record.projectName
          if (!projectId && !projectName) {
            this.$message.warning('缺少项目信息，无法查看手环详情')
            return
          }
          this.currentWristbandRecord = record
          this.currentWristbandProjectId = projectId
          this.currentWristbandProjectName = projectName
          this.wristbandDetailPageNum = 1
          this.wristbandDetailPageSize = 10
          this.wristbandDetailData = []
          this.wristbandDetailTotal = 0
          this.wristbandDetailLoading = true
          this.wristbandDetailVisible = true
          this.wristbandDetailRawData = this.resolveWristbandDetailSource(record)
          const today = (this.$moment && this.$moment().format('YYYY-MM-DD')) || moment().format('YYYY-MM-DD')
          if (this.startTime && this.endTime) {
            this.wristbandDetailFilterType = 'custom'
            this.wristbandDetailDateRange = [this.startTime, this.endTime]
            this.wristbandDetailStartTime = this.startTime
            this.wristbandDetailEndTime = this.endTime
          } else {
            this.wristbandDetailFilterType = 'today'
            this.wristbandDetailDateRange = [today, today]
            this.wristbandDetailStartTime = today
            this.wristbandDetailEndTime = today
          }
          // 首次数据由 watch wristbandDetailVisible 在弹窗显示后触发 fetchWristbandDetail
        },
        // 手环详情筛选切换（与 index.vue 一致）：日期回显到日期组件并请求筛选数据
        handleWristbandDetailFilterChange(type) {
          this.wristbandDetailFilterType = type
          const today = moment().format('YYYY-MM-DD')
          let start, end
          switch (type) {
            case 'today':
              start = today
              end = today
              this.wristbandDetailDateRange = [start, end]
              break
            case 'week':
              start = moment().startOf('week').format('YYYY-MM-DD')
              end = moment().endOf('week').format('YYYY-MM-DD')
              this.wristbandDetailDateRange = [start, end]
              break
            case 'month':
              start = moment().startOf('month').format('YYYY-MM-DD')
              end = moment().endOf('month').format('YYYY-MM-DD')
              this.wristbandDetailDateRange = [start, end]
              break
            case 'custom':
              if (this.wristbandDetailDateRange && this.wristbandDetailDateRange.length === 2) {
                this.wristbandDetailStartTime = this.normalizeDateValue(
                  this.wristbandDetailDateRange[0]
                )
                this.wristbandDetailEndTime = this.normalizeDateValue(
                  this.wristbandDetailDateRange[1]
                )
              } else {
                this.wristbandDetailStartTime = undefined
                this.wristbandDetailEndTime = undefined
              }
              this.wristbandDetailPageNum = 1
              this.fetchWristbandDetail()
              return
          }
          this.wristbandDetailStartTime = start
          this.wristbandDetailEndTime = end
          this.wristbandDetailPageNum = 1
          this.fetchWristbandDetail()
        },
        handleWristbandDetailDateRangeChange(dates, dateStrings) {
          if (!dates || dates.length === 0) {
            this.wristbandDetailStartTime = undefined
            this.wristbandDetailEndTime = undefined
          } else {
            this.wristbandDetailStartTime = dateStrings[0] || undefined
            this.wristbandDetailEndTime = dateStrings[1] || undefined
          }
          if (this.wristbandDetailFilterType === 'custom') {
            this.fetchWristbandDetail()
          }
        },
        resolveWristbandDetailSource(record) {
          const source =
            record.wristbandDetailList ||
            record.healthDetailList ||
            record.healthDetails ||
            record.detailList ||
            record.details ||
            record.records ||
            record.items ||
            record.list ||
            []

          return Array.isArray(source) ? source : []
        },
        normalizeWristbandDetailItem(item, index) {
          return {
            ...item,
            index,
            employeeName:
              item.employeeName || item.nickname || item.name || item.userName || '--',
            mac: item.mac || item.wristbandMac || '--',
            heartbeat:
              item.heartbeat !== undefined && item.heartbeat !== null
                ? item.heartbeat
                : item.heartRate !== undefined && item.heartRate !== null
                  ? item.heartRate
                  : item.heart ?? '--',
            systolicBp:
              item.systolicBp !== undefined && item.systolicBp !== null
                ? item.systolicBp
                : undefined,
            diastolicBp:
              item.diastolicBp !== undefined && item.diastolicBp !== null
                ? item.diastolicBp
                : undefined,
            temperature:
              item.temperature !== undefined && item.temperature !== null
                ? item.temperature
                : item.bodyTemperature || '--',
            healthUpdateTime:
              item.healthUpdateTime || item.updateTime || item.createTime || '--',
          }
        },
        filterWristbandDetailByDate(records) {
          if (!this.wristbandDetailStartTime || !this.wristbandDetailEndTime) {
            return records
          }
          const start = moment(this.wristbandDetailStartTime, 'YYYY-MM-DD').startOf('day')
          const end = moment(this.wristbandDetailEndTime, 'YYYY-MM-DD').endOf('day')
          return records.filter((item) => {
            const timeValue =
              item.healthUpdateTime || item.updateTime || item.createTime || item.taskTime
            if (!timeValue) {
              return true
            }
            const current = moment(timeValue)
            if (!current.isValid()) {
              return true
            }
            return current.isBetween(start, end, null, '[]')
          })
        },
        fetchWristbandDetail() {
          const hasProjectId = !!this.currentWristbandProjectId
          const hasProjectName = !!this.currentWristbandProjectName
          if (!hasProjectId && !hasProjectName) {
            this.wristbandDetailData = []
            this.wristbandDetailTotal = 0
            this.wristbandDetailLoading = false
            return
          }

          this.wristbandDetailLoading = true
          const rawList = Array.isArray(this.wristbandDetailRawData) ? this.wristbandDetailRawData : []
          const buildLocalPage = () => {
            try {
              const filtered = this.filterWristbandDetailByDate(rawList)
              const records = filtered.map((item, index) =>
                this.normalizeWristbandDetailItem(
                  item,
                  (this.wristbandDetailPageNum - 1) * this.wristbandDetailPageSize + index + 1
                )
              )
              this.wristbandDetailTotal = records.length
              const startIndex = (this.wristbandDetailPageNum - 1) * this.wristbandDetailPageSize
              const endIndex = startIndex + this.wristbandDetailPageSize
              this.wristbandDetailData = records.slice(startIndex, endIndex)
            } catch (e) {
              this.wristbandDetailData = []
              this.wristbandDetailTotal = 0
            }
            this.wristbandDetailLoading = false
          }

          const params = {
            pageNum: this.wristbandDetailPageNum,
            pageSize: this.wristbandDetailPageSize,
          }
          if (this.currentWristbandProjectId) {
            params.projectId = this.currentWristbandProjectId
          }
          if (this.currentWristbandProjectName) {
            params.projectName = this.currentWristbandProjectName
          }
          if (this.wristbandDetailStartTime) {
            params.startTime = this.wristbandDetailStartTime
          }
          if (this.wristbandDetailEndTime) {
            params.endTime = this.wristbandDetailEndTime
          }

          const stopLoading = () => {
            this.wristbandDetailLoading = false
          }
          try {
            const requestPromise = getWristbandPage(params)
            const timeoutPromise = new Promise((_, reject) =>
              setTimeout(() => reject(new Error('timeout')), 8000)
            )
            Promise.race([requestPromise, timeoutPromise])
              .then((res) => {
                try {
                  const page = res?.data !== undefined ? res.data : res || {}
                  const records = page.records ?? page.rows ?? []
                  const list = Array.isArray(records) ? records : []
                  this.wristbandDetailTotal = Number(page.total) || 0
                  this.wristbandDetailData = list.map((item, index) =>
                    this.normalizeWristbandDetailItem(
                      item,
                      (this.wristbandDetailPageNum - 1) * this.wristbandDetailPageSize + index + 1
                    )
                  )
                } catch (e) {
                  this.wristbandDetailData = []
                  this.wristbandDetailTotal = 0
                }
              })
              .catch((err) => {
                if (err?.message === 'timeout') {
                  this.$message.warning('请求超时，请稍后重试')
                  this.wristbandDetailData = []
                  this.wristbandDetailTotal = 0
                } else {
                  try {
                    buildLocalPage()
                  } catch (e) {
                    this.wristbandDetailData = []
                    this.wristbandDetailTotal = 0
                  }
                }
                stopLoading()
              })
              .finally(stopLoading)
          } catch (e) {
            this.wristbandDetailData = []
            this.wristbandDetailTotal = 0
            stopLoading()
          }
        },
        handleWristbandDetailPageChange(page, pageSize) {
          this.wristbandDetailPageNum = page
          this.wristbandDetailPageSize = pageSize
          this.fetchWristbandDetail()
        },
        handleWristbandDetailSizeChange(current, size) {
          this.wristbandDetailPageNum = 1
          this.wristbandDetailPageSize = size
          this.fetchWristbandDetail()
        },
        getWristbandDetailRowKey(record, index) {
          return (
            record.id ||
            `${record.employeeName || record.nickname || index}-${record.mac || ''}-${index}`
          )
        },
        handleWristbandDetailClose() {
          this.wristbandDetailVisible = false
          this.currentWristbandRecord = null
          this.currentWristbandProjectId = undefined
          this.currentWristbandProjectName = undefined
          this.wristbandDetailRawData = []
          this.wristbandDetailData = []
          this.wristbandDetailPageNum = 1
          this.wristbandDetailPageSize = 10
          this.wristbandDetailTotal = 0
          this.wristbandDetailFilterType = 'today'
          this.wristbandDetailDateRange = []
          this.wristbandDetailStartTime = undefined
          this.wristbandDetailEndTime = undefined
        },
        openAttendanceDetail(record) {
          if (!record) {
            return
          }
          const projectId = record.attendanceId || this.resolveProjectId(record)
          const projectName = record.name || record.projectName
          if (!projectId && !projectName) {
            this.$message.warning('缺少项目信息，无法查看出勤详情')
            return
          }
          this.currentAttendanceRecord = record
          this.currentAttendanceProjectId = projectId
          this.currentAttendanceProjectName = projectName
          this.attendanceDetailPageNum = 1
          this.attendanceDetailPageSize = 10
          this.attendanceDetailVisible = true
          this.attendanceDetailRawData = this.resolveAttendanceDetailSource(record)
          const today = (this.$moment && this.$moment().format('YYYY-MM-DD')) || moment().format('YYYY-MM-DD')
          if (this.startTime && this.endTime) {
            this.attendanceDetailFilterType = 'custom'
            this.attendanceDetailDateRange = [this.startTime, this.endTime]
            this.attendanceDetailStartTime = this.startTime
            this.attendanceDetailEndTime = this.endTime
          } else {
            this.attendanceDetailFilterType = 'today'
            this.attendanceDetailDateRange = [today, today]
            this.attendanceDetailStartTime = today
            this.attendanceDetailEndTime = today
          }
          // 首次数据由 watch attendanceDetailVisible 在弹窗显示后触发 fetchAttendanceDetail
        },
        // 出勤详情筛选切换（与 index.vue 手环详情一致）：日期回显到日期组件并请求筛选数据
        handleAttendanceDetailFilterChange(type) {
          this.attendanceDetailFilterType = type
          const today = moment().format('YYYY-MM-DD')
          let start, end
          switch (type) {
            case 'today':
              start = today
              end = today
              this.attendanceDetailDateRange = [start, end]
              break
            case 'week':
              start = moment().startOf('week').format('YYYY-MM-DD')
              end = moment().endOf('week').format('YYYY-MM-DD')
              this.attendanceDetailDateRange = [start, end]
              break
            case 'month':
              start = moment().startOf('month').format('YYYY-MM-DD')
              end = moment().endOf('month').format('YYYY-MM-DD')
              this.attendanceDetailDateRange = [start, end]
              break
            case 'custom':
              if (this.attendanceDetailDateRange && this.attendanceDetailDateRange.length === 2) {
                this.attendanceDetailStartTime = this.normalizeDateValue(
                  this.attendanceDetailDateRange[0]
                )
                this.attendanceDetailEndTime = this.normalizeDateValue(
                  this.attendanceDetailDateRange[1]
                )
              } else {
                this.attendanceDetailStartTime = undefined
                this.attendanceDetailEndTime = undefined
              }
              this.attendanceDetailPageNum = 1
              this.fetchAttendanceDetail()
              return
          }
          this.attendanceDetailStartTime = start
          this.attendanceDetailEndTime = end
          this.attendanceDetailPageNum = 1
          this.fetchAttendanceDetail()
        },
        handleAttendanceDetailDateRangeChange(dates, dateStrings) {
          if (!dates || dates.length === 0) {
            this.attendanceDetailStartTime = undefined
            this.attendanceDetailEndTime = undefined
          } else {
            this.attendanceDetailStartTime = dateStrings[0] || undefined
            this.attendanceDetailEndTime = dateStrings[1] || undefined
          }
          if (this.attendanceDetailFilterType === 'custom') {
            this.fetchAttendanceDetail()
          }
        },
        resolveAttendanceDetailSource(record) {
          const source =
            record.attendanceDetailList ||
            record.employeeList ||
            record.employeeRecords ||
            record.records ||
            record.items ||
            record.list ||
            []

          return Array.isArray(source) ? source : []
        },
        normalizeAttendanceDetailItem(item, index) {
          return {
            ...item,
            index,
            employeeName:
              item.employeeName || item.nickname || item.name || item.userName || '--',
            currentLocation:
              item.currentLocation ||
              item.locationName ||
              item.positionName ||
              item.spaceName ||
              item.jobName ||
              '--',
            locationUpdateTime:
              item.uploadTime || item.updateTime || item.createTime || '--',
            hasWorkflow: item.hasWorkflow != null && item.hasWorkflow !== '' ? item.hasWorkflow : '-',
            workflowStarted: item.workflowStarted != null && item.workflowStarted !== '' ? item.workflowStarted : '-',
          }
        },
        filterAttendanceDetailByDate(records) {
          if (!this.attendanceDetailStartTime || !this.attendanceDetailEndTime) {
            return records
          }
          const start = moment(this.attendanceDetailStartTime, 'YYYY-MM-DD').startOf('day')
          const end = moment(this.attendanceDetailEndTime, 'YYYY-MM-DD').endOf('day')
          return records.filter((item) => {
            const timeValue =
              item.uploadTime || item.updateTime || item.createTime || item.taskTime
            if (!timeValue) {
              return true
            }
            const current = moment(timeValue)
            if (!current.isValid()) {
              return true
            }
            return current.isBetween(start, end, null, '[]')
          })
        },
        fetchAttendanceDetail() {
          const hasProjectId = !!this.currentAttendanceProjectId
          const hasProjectName = !!this.currentAttendanceProjectName
          if (!hasProjectId && !hasProjectName && !this.attendanceDetailRawData.length) {
            this.attendanceDetailData = []
            this.attendanceDetailTotal = 0
            return
          }

          this.attendanceDetailLoading = true
          const buildLocalPage = () => {
            const records = this.filterAttendanceDetailByDate(this.attendanceDetailRawData).map(
              (item, index) =>
                this.normalizeAttendanceDetailItem(
                  item,
                  (this.attendanceDetailPageNum - 1) * this.attendanceDetailPageSize + index + 1
                )
            )
            this.attendanceDetailTotal = records.length
            const startIndex = (this.attendanceDetailPageNum - 1) * this.attendanceDetailPageSize
            const endIndex = startIndex + this.attendanceDetailPageSize
            this.attendanceDetailData = records.slice(startIndex, endIndex)
            this.attendanceDetailLoading = false
          }

          if (!hasProjectId && !hasProjectName) {
            buildLocalPage()
            return
          }

          const params = {
            pageNum: this.attendanceDetailPageNum,
            pageSize: this.attendanceDetailPageSize,
          }
          if (this.currentAttendanceProjectId) {
            params.projectId = this.currentAttendanceProjectId
          }
          if (this.currentAttendanceProjectName) {
            params.projectName = this.currentAttendanceProjectName
          }
          if (this.attendanceDetailStartTime) {
            params.startTime = this.attendanceDetailStartTime
          }
          if (this.attendanceDetailEndTime) {
            params.endTime = this.attendanceDetailEndTime
          }
          getAttendancePage(params)
            .then((res) => {
              const page = res?.data || {}
              const records = page.records || []
              this.attendanceDetailTotal = page.total || 0
              this.attendanceDetailData = records.map((item, index) =>
                this.normalizeAttendanceDetailItem(
                  item,
                  (this.attendanceDetailPageNum - 1) * this.attendanceDetailPageSize + index + 1
                )
              )
            })
            .catch(() => {
              buildLocalPage()
            })
            .finally(() => {
              this.attendanceDetailLoading = false
            })
        },
        handleAttendanceDetailPageChange(page, pageSize) {
          this.attendanceDetailPageNum = page
          this.attendanceDetailPageSize = pageSize
          this.fetchAttendanceDetail()
        },
        handleAttendanceDetailSizeChange(current, size) {
          this.attendanceDetailPageNum = 1
          this.attendanceDetailPageSize = size
          this.fetchAttendanceDetail()
        },
        getAttendanceDetailRowKey(record, index) {
          return (
            record.userId ||
            record.id ||
            `${record.employeeName || record.nickname || index}-${record.currentLocation || ''}-${index}`
          )
        },
        handleAttendanceDetailClose() {
          this.attendanceDetailVisible = false
          this.currentAttendanceRecord = null
          this.currentAttendanceProjectId = undefined
          this.currentAttendanceProjectName = undefined
          this.attendanceDetailRawData = []
          this.attendanceDetailData = []
          this.attendanceDetailPageNum = 1
          this.attendanceDetailPageSize = 10
          this.attendanceDetailTotal = 0
          this.attendanceDetailFilterType = 'today'
          this.attendanceDetailDateRange = []
          this.attendanceDetailStartTime = undefined
          this.attendanceDetailEndTime = undefined
        },
        openComplaintDetail(record) {
          if (!record) {
            return
          }
          const projectId = this.resolveProjectId(record)
          const projectName = record.name || record.projectName
          if (!projectId && !projectName) {
            this.$message.warning('缺少项目信息，无法查看投诉详情')
            return
          }
          this.currentComplaintProjectId = projectId
          this.currentComplaintProjectName = projectName
          this.complaintDetailPageNum = 1
          this.complaintDetailPageSize = 10
          this.complaintDetailVisible = true
          this.complaintDetailRawData = this.resolveComplaintDetailSource(record)
          const today = (this.$moment && this.$moment().format('YYYY-MM-DD')) || moment().format('YYYY-MM-DD')
          if (this.startTime && this.endTime) {
            this.complaintDetailFilterType = 'custom'
            this.complaintDetailDateRange = [this.startTime, this.endTime]
            this.complaintDetailStartTime = this.startTime
            this.complaintDetailEndTime = this.endTime
          } else {
            this.complaintDetailFilterType = 'today'
            this.complaintDetailDateRange = [today, today]
            this.complaintDetailStartTime = today
            this.complaintDetailEndTime = today
          }
          // 首次数据由 watch complaintDetailVisible 在弹窗显示后触发 fetchComplaintDetail
        },
        // 投诉详情筛选切换（与 index.vue 手环详情一致）：日期回显到日期组件并请求筛选数据
        handleComplaintDetailFilterChange(type) {
          this.complaintDetailFilterType = type
          const today = moment().format('YYYY-MM-DD')
          let start, end
          switch (type) {
            case 'today':
              start = today
              end = today
              this.complaintDetailDateRange = [start, end]
              break
            case 'week':
              start = moment().startOf('week').format('YYYY-MM-DD')
              end = moment().endOf('week').format('YYYY-MM-DD')
              this.complaintDetailDateRange = [start, end]
              break
            case 'month':
              start = moment().startOf('month').format('YYYY-MM-DD')
              end = moment().endOf('month').format('YYYY-MM-DD')
              this.complaintDetailDateRange = [start, end]
              break
            case 'custom':
              if (this.complaintDetailDateRange && this.complaintDetailDateRange.length === 2) {
                this.complaintDetailStartTime = this.normalizeDateValue(
                  this.complaintDetailDateRange[0]
                )
                this.complaintDetailEndTime = this.normalizeDateValue(
                  this.complaintDetailDateRange[1]
                )
              } else {
                this.complaintDetailStartTime = undefined
                this.complaintDetailEndTime = undefined
              }
              this.complaintDetailPageNum = 1
              this.fetchComplaintDetail()
              return
          }
          this.complaintDetailStartTime = start
          this.complaintDetailEndTime = end
          this.complaintDetailPageNum = 1
          this.fetchComplaintDetail()
        },
        handleComplaintDetailDateRangeChange(dates, dateStrings) {
          if (!dates || dates.length === 0) {
            this.complaintDetailStartTime = undefined
            this.complaintDetailEndTime = undefined
          } else {
            this.complaintDetailStartTime = dateStrings[0] || undefined
            this.complaintDetailEndTime = dateStrings[1] || undefined
          }
          if (this.complaintDetailFilterType === 'custom') {
            this.fetchComplaintDetail()
          }
        },
        resolveComplaintDetailSource(record) {
          const source =
            record.complaintDetailList ||
            record.complaintOrders ||
            record.detailList ||
            record.details ||
            record.records ||
            record.items ||
            record.list ||
            []

          return Array.isArray(source) ? source : []
        },
        formatComplaintStatus(status) {
          const statusMap = {
            0: '待处理',
            1: '处理中',
            2: '已完成',
          }
          return statusMap[status] || statusMap[String(status)] || status || '--'
        },
        formatComplaintType(type) {
          const typeMap = {
            0: '投诉',
            1: '任务需求',
            2: '临时任务',
            3: '巡检单',
          }
          return typeMap[type] || typeMap[String(type)] || type || '--'
        },
        handleComplaintDetailExtraFilterChange() {
          this.complaintDetailPageNum = 1
          this.fetchComplaintDetail()
        },
        normalizeComplaintDetailItem(item, index) {
          const ownerName =
            item.nickname ||
            item.employeeName ||
            item.supervisorName ||
            item.confirmName ||
            item.closingName ||
            '--'
          return {
            index,
            spaceName: item.spaceName || item.name || '--',
            typeText: this.formatComplaintType(item.type),
            complaintStatusText: this.formatComplaintStatus(
              item.complaintStatus ?? item.status
            ),
            nickname: ownerName,
            complaintTimeText:
              item.createTime || item.complaintTime || item.responseTime || '--',
            descriptionText: item.description || item.content || '--',
            ...item,
          }
        },
        filterComplaintDetailByDate(records) {
          if (!this.complaintDetailStartTime || !this.complaintDetailEndTime) {
            return records
          }
          const start = moment(this.complaintDetailStartTime, 'YYYY-MM-DD').startOf('day')
          const end = moment(this.complaintDetailEndTime, 'YYYY-MM-DD').endOf('day')
          return records.filter((item) => {
            const timeValue =
              item.createTime || item.complaintTime || item.responseTime || item.updateTime
            if (!timeValue) {
              return true
            }
            const current = moment(timeValue)
            if (!current.isValid()) {
              return true
            }
            return current.isBetween(start, end, null, '[]')
          })
        },
        fetchComplaintDetail() {
          const hasProjectId = !!this.currentComplaintProjectId
          const hasProjectName = !!this.currentComplaintProjectName
          if (!hasProjectId && !hasProjectName && !this.complaintDetailRawData.length) {
            this.complaintDetailData = []
            this.complaintDetailTotal = 0
            return
          }

          this.complaintDetailLoading = true
          const buildLocalPage = () => {
            const records = this.filterComplaintDetailByDate(this.complaintDetailRawData).map(
              (item, index) =>
                this.normalizeComplaintDetailItem(
                  item,
                  (this.complaintDetailPageNum - 1) * this.complaintDetailPageSize + index + 1
                )
            )
            this.complaintDetailTotal = records.length
            const startIndex = (this.complaintDetailPageNum - 1) * this.complaintDetailPageSize
            const endIndex = startIndex + this.complaintDetailPageSize
            this.complaintDetailData = records.slice(startIndex, endIndex)
            this.complaintDetailLoading = false
          }

          if (!hasProjectId && !hasProjectName) {
            buildLocalPage()
            return
          }

          const params = {
            pageNum: this.complaintDetailPageNum,
            pageSize: this.complaintDetailPageSize,
          }
          if (this.currentComplaintProjectId) {
            params.projectId = this.currentComplaintProjectId
          }
          if (this.currentComplaintProjectName) {
            params.projectName = this.currentComplaintProjectName
          }
          if (this.complaintDetailStartTime) {
            params.startTime = this.complaintDetailStartTime
          }
          if (this.complaintDetailEndTime) {
            params.endTime = this.complaintDetailEndTime
          }
          if (this.complaintDetailTypeFilter !== undefined && this.complaintDetailTypeFilter !== null && this.complaintDetailTypeFilter !== '') {
            params.type = this.complaintDetailTypeFilter
          }
          if (this.complaintDetailStatusFilter !== undefined && this.complaintDetailStatusFilter !== null && this.complaintDetailStatusFilter !== '') {
            params.complaintStatus = this.complaintDetailStatusFilter
          }
          getComplaintPage(params)
            .then((res) => {
              const page = res?.data || {}
              const records = page.records || []
              this.complaintDetailTotal = page.total || 0
              this.complaintDetailData = records.map((item, index) =>
                this.normalizeComplaintDetailItem(
                  item,
                  (this.complaintDetailPageNum - 1) * this.complaintDetailPageSize + index + 1
                )
              )
            })
            .catch(() => {
              buildLocalPage()
            })
            .finally(() => {
              this.complaintDetailLoading = false
            })
        },
        handleComplaintDetailPageChange(page, pageSize) {
          this.complaintDetailPageNum = page
          this.complaintDetailPageSize = pageSize
          this.fetchComplaintDetail()
        },
        handleComplaintDetailSizeChange(current, size) {
          this.complaintDetailPageNum = 1
          this.complaintDetailPageSize = size
          this.fetchComplaintDetail()
        },
        getComplaintDetailRowKey(record, index) {
          return (
            record.id ||
            `${record.spaceName || index}-${record.description || record.content || ''}-${index}`
          )
        },
        handleComplaintDetailClose() {
          this.complaintDetailVisible = false
          this.currentComplaintProjectId = undefined
          this.currentComplaintProjectName = undefined
          this.complaintDetailRawData = []
          this.complaintDetailData = []
          this.complaintDetailPageNum = 1
          this.complaintDetailPageSize = 10
          this.complaintDetailTotal = 0
          this.complaintDetailFilterType = 'today'
          this.complaintDetailDateRange = []
          this.complaintDetailStartTime = undefined
          this.complaintDetailEndTime = undefined
          this.complaintDetailTypeFilter = undefined
          this.complaintDetailStatusFilter = undefined
        },
        openHealthAlertDetail(record) {
          if (!record) {
            return
          }
          const projectId = this.resolveProjectId(record)
          const projectName = record.name || record.projectName
          if (!projectId && !projectName) {
            this.$message.warning('缺少项目信息，无法查看健康预警详情')
            return
          }
          this.currentHealthAlertProjectId = projectId
          this.currentHealthAlertProjectName = projectName
          this.healthAlertDetailPageNum = 1
          this.healthAlertDetailPageSize = 10
          this.healthAlertDetailVisible = true
          const today = (this.$moment && this.$moment().format('YYYY-MM-DD')) || moment().format('YYYY-MM-DD')
          if (this.startTime && this.endTime) {
            this.healthAlertDetailFilterType = 'custom'
            this.healthAlertDetailDateRange = [this.startTime, this.endTime]
            this.healthAlertDetailStartTime = this.startTime
            this.healthAlertDetailEndTime = this.endTime
          } else {
            this.healthAlertDetailFilterType = 'today'
            this.healthAlertDetailDateRange = [today, today]
            this.healthAlertDetailStartTime = today
            this.healthAlertDetailEndTime = today
          }
          // 首次数据由 watch healthAlertDetailVisible 在弹窗显示后触发 fetchHealthAlertDetail
        },
        // 健康预警详情筛选切换（与 index.vue 手环详情一致）：日期回显到日期组件并请求筛选数据
        handleHealthAlertDetailFilterChange(type) {
          this.healthAlertDetailFilterType = type
          const today = moment().format('YYYY-MM-DD')
          let start, end
          switch (type) {
            case 'today':
              start = today
              end = today
              this.healthAlertDetailDateRange = [start, end]
              break
            case 'week':
              start = moment().startOf('week').format('YYYY-MM-DD')
              end = moment().endOf('week').format('YYYY-MM-DD')
              this.healthAlertDetailDateRange = [start, end]
              break
            case 'month':
              start = moment().startOf('month').format('YYYY-MM-DD')
              end = moment().endOf('month').format('YYYY-MM-DD')
              this.healthAlertDetailDateRange = [start, end]
              break
            case 'custom':
              if (
                this.healthAlertDetailDateRange &&
                this.healthAlertDetailDateRange.length === 2
              ) {
                this.healthAlertDetailStartTime = this.normalizeDateValue(
                  this.healthAlertDetailDateRange[0]
                )
                this.healthAlertDetailEndTime = this.normalizeDateValue(
                  this.healthAlertDetailDateRange[1]
                )
              } else {
                this.healthAlertDetailStartTime = undefined
                this.healthAlertDetailEndTime = undefined
              }
              this.healthAlertDetailPageNum = 1
              this.fetchHealthAlertDetail()
              return
          }
          this.healthAlertDetailStartTime = start
          this.healthAlertDetailEndTime = end
          this.healthAlertDetailPageNum = 1
          this.fetchHealthAlertDetail()
        },
        handleHealthAlertDetailDateRangeChange(dates, dateStrings) {
          if (!dates || dates.length === 0) {
            this.healthAlertDetailStartTime = undefined
            this.healthAlertDetailEndTime = undefined
          } else {
            this.healthAlertDetailStartTime = dateStrings[0] || undefined
            this.healthAlertDetailEndTime = dateStrings[1] || undefined
          }
          if (this.healthAlertDetailFilterType === 'custom') {
            this.fetchHealthAlertDetail()
          }
        },
        normalizeHealthAlertDetailItem(item, index) {
          return {
            ...item,
            index,
            employeeName:
              item.employeeName || item.nickname || item.name || item.userName || '--',
            metricData:
              item.metricData ??
              item.alertData ??
              item.warningData ??
              item.dataValue ??
              item.content ??
              '--',
            metricName:
              item.metricName ??
              item.alertType ??
              item.warningType ??
              item.type ??
              '--',
            uploadTime:
              item.uploadTime ??
              item.alertTime ??
              item.warningTime ??
              item.createTime ??
              item.updateTime ??
              '--',
          }
        },
        fetchHealthAlertDetail() {
          const hasProjectId = !!this.currentHealthAlertProjectId
          const hasProjectName = !!this.currentHealthAlertProjectName
          if (!hasProjectId && !hasProjectName) {
            this.healthAlertDetailData = []
            this.healthAlertDetailTotal = 0
            return
          }

          this.healthAlertDetailLoading = true
          const params = {
            pageNum: this.healthAlertDetailPageNum,
            pageSize: this.healthAlertDetailPageSize,
          }
          if (this.currentHealthAlertProjectId) {
            params.projectId = this.currentHealthAlertProjectId
          }
          if (this.currentHealthAlertProjectName) {
            params.projectName = this.currentHealthAlertProjectName
          }
          if (this.healthAlertDetailStartTime) {
            params.startTime = this.healthAlertDetailStartTime
          }
          if (this.healthAlertDetailEndTime) {
            params.endTime = this.healthAlertDetailEndTime
          }

          getHealthAlertPage(params)
            .then((res) => {
              const page = res?.data || {}
              const records = page.records || []
              this.healthAlertDetailTotal = page.total || 0
              this.healthAlertDetailData = records.map((item, index) =>
                this.normalizeHealthAlertDetailItem(
                  item,
                  (this.healthAlertDetailPageNum - 1) * this.healthAlertDetailPageSize + index + 1
                )
              )
            })
            .catch(() => {
              this.healthAlertDetailData = []
              this.healthAlertDetailTotal = 0
              this.$message.error('获取健康预警详情失败')
            })
            .finally(() => {
              this.healthAlertDetailLoading = false
            })
        },
        handleHealthAlertDetailPageChange(page, pageSize) {
          this.healthAlertDetailPageNum = page
          this.healthAlertDetailPageSize = pageSize
          this.fetchHealthAlertDetail()
        },
        handleHealthAlertDetailSizeChange(current, size) {
          this.healthAlertDetailPageNum = 1
          this.healthAlertDetailPageSize = size
          this.fetchHealthAlertDetail()
        },
        getHealthAlertDetailRowKey(record, index) {
          return (
            record.id ||
            `${record.employeeName || record.nickname || index}-${record.alertTime || record.createTime || ''}-${index}`
          )
        },
        handleHealthAlertDetailClose() {
          this.healthAlertDetailVisible = false
          this.currentHealthAlertProjectId = undefined
          this.currentHealthAlertProjectName = undefined
          this.healthAlertDetailData = []
          this.healthAlertDetailPageNum = 1
          this.healthAlertDetailPageSize = 10
          this.healthAlertDetailTotal = 0
          this.healthAlertDetailFilterType = 'today'
          this.healthAlertDetailDateRange = []
          this.healthAlertDetailStartTime = undefined
          this.healthAlertDetailEndTime = undefined
        },
  },
}


</script>

<style scoped>
:deep(.ant-pro-page-header-wrap-page-header-warp) {
  display: none !important;
}

:deep(.ant-pro-page-header-wrap-children-content) {
  margin-top: 0 !important;
}

      .dashboard-container {
        padding: 16px;
        width: 100%;
        min-width: 0;
        overflow-x: hidden;
        /* background: #f5f5f5; */
      }
      .section-card {
        border-radius: 8px 8px 8px 8px;
        overflow: hidden;
        width: 100%;
        min-width: 0;
        background: #ffffff;
        box-shadow: none !important;
        border: none !important;
        margin-bottom: 0;
      }
      .task-detail-header {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin-top: 12px;
        margin-bottom: 16px;
      }
      .task-detail-header-row {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
      }
      .task-detail-status-row {
        margin-top: 0;
      }
      .task-detail-filter-label {
        font-size: 14px;
        color: #666;
        margin-right: 8px;
      }
      .task-detail-user-select {
        min-width: 220px;
        max-width: 320px;
      }
      .task-detail-status-select {
        border-radius: 4px;
        :deep(.ant-select-selector),
        :deep(.javaMain-select-selector) {
          border-radius: 4px !important;
        }
      }
      .task-detail-header-right {
        margin-left: auto;
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
      }
      /* 投诉详情：筛选单行不换行 */
      .complaint-detail-header {
        flex-direction: row !important;
        flex-wrap: nowrap !important;
      }
      .complaint-detail-header .filter-buttons {
        flex-shrink: 0;
      }
      .complaint-detail-header .task-detail-header-right {
        flex-wrap: nowrap !important;
        flex-shrink: 0;
        min-width: 0;
      }
      /* 健康预警详情：筛选单行不换行 */
      .health-alert-detail-header {
        flex-direction: row !important;
        flex-wrap: nowrap !important;
      }
      .health-alert-detail-header .filter-buttons {
        flex-shrink: 0;
      }
      .health-alert-detail-header .task-detail-header-right {
        flex-wrap: nowrap !important;
        flex-shrink: 0;
        min-width: 0;
      }
      .wristband-detail-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 16px;
        flex-wrap: wrap;
        margin-top: 10px;
        margin-bottom: 16px;
      }
      .wristband-detail-header-right {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
      }
      .wristband-detail-custom-btn {
        border-radius: 4px !important;
        background: #ffffff !important;
        color: #000000 !important;
        border-color: #d9d9d9 !important;
      }
      .wristband-detail-custom-btn:hover {
        background: #ffffff !important;
        border-color: #d9d9d9 !important;
        color: #000000 !important;
      }
      .attendance-detail-custom-btn {
        border-radius: 4px !important;
        background: #ffffff !important;
        color: #000000 !important;
        border-color: #d9d9d9 !important;
        box-shadow: none !important;
        outline: none !important;
      }
      .attendance-detail-custom-btn:hover,
      .attendance-detail-custom-btn:focus {
        background: #ffffff !important;
        border-color: #d9d9d9 !important;
        color: #000000 !important;
        box-shadow: none !important;
        outline: none !important;
      }
      .health-alert-detail-custom-btn {
        border-radius: 4px !important;
        background: #ffffff !important;
        color: #000000 !important;
        border-color: #d9d9d9 !important;
        box-shadow: none !important;
        outline: none !important;
      }
      .health-alert-detail-custom-btn:focus {
        background: #ffffff !important;
        color: #000000 !important;
        border-color: #d9d9d9 !important;
        box-shadow: none !important;
        outline: none !important;
      }
      .task-detail-date-picker {
        min-width: 240px;
        border-radius: 4px;
      }
      .task-detail-date-picker :deep(.ant-picker) {
        border-radius: 4px;
      }
      .task-detail-footer-pagination-wrapper {
        display: flex;
        justify-content: flex-end;
        padding: 16px 0 0;
      }
      .wristband-detail-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        flex-wrap: wrap;
        gap: 12px;
      }
      .wristband-detail-footer .task-detail-footer-pagination-wrapper {
        margin-left: auto;
      }
      .wristband-detail-footer-left {
        color: #666666;
        font-size: 14px;
        padding-top: 16px;
      }

      /* 自定义时间按钮样式，选中后去掉黑色边框 */
      .custom-date-radio :deep(.ant-radio-button-wrapper) {
        border-radius: 4px !important;
      }
      .custom-date-radio :deep(.ant-radio-button-wrapper-checked) {
        background-color: #ffffff !important;
        border-color: #d9d9d9 !important;
        color: #000000 !important;
        box-shadow: none !important;
        outline: none !important;
      }
      .custom-date-radio :deep(.ant-radio-button-wrapper-checked::before) {
        background-color: #d9d9d9 !important;
      }
      .custom-date-radio :deep(.ant-radio-button-wrapper:focus-within) {
        box-shadow: none !important;
        outline: none !important;
      }

      /* 顶部筛选按钮通用样式（任务详情 / 手环详情等） */
      .filter-buttons :deep(.ant-btn) {
        border-radius: 4px !important;
      }
      .filter-buttons :deep(.ant-btn-primary) {
        background-color: #165dff !important;
        border-color: #165dff !important;
        color: #ffffff !important;
      }

      .task-detail-pagination {
        display: none; /* 隐藏原有的分页，使用 footer 中的分页 */
      }
      .task-detail-pagination {
        display: none; /* 隐藏原有的分页，使用 footer 中的分页 */
      }

/* 确保每个模块之间有16px间距 - 统一使用 margin-bottom */
.project-summary-card {
  margin-bottom: 16px;
}

.dashboard-container > .ant-row {
  margin-bottom: 16px;
  margin-top: 0;
}

.dashboard-container > .ant-row:last-child {
  margin-bottom: 0;
}

.section-card :deep(.ant-card) {
  box-shadow: none !important;
  border: none !important;
  border-radius: 8px 8px 8px 8px !important;
}
.table-wrapper {
  width: 100%;
  overflow-x: auto;
}
.section-card :deep(.ant-card-head) {
  border-bottom: none;
  padding: 20px 20px 0 20px;
  margin-top: 0;
  background: #ffffff;
}
.section-card :deep(.ant-card-head-title) {
  font-family: PingFang SC, PingFang SC;
  font-weight: bold;
  font-size: 18px;
  color: #101010;
  text-align: left;
  font-style: normal;
  text-transform: none;
  padding: 0;
  margin-bottom: 16px;
}
.section-card :deep(.ant-card-body) {
  padding: 0 20px 20px 20px;
  margin-top: 0;
  background: #ffffff;
}
.section-card :deep(.ant-table) {
  background: #ffffff;
}
.section-card :deep(.ant-table-thead > tr > th) {
  background: #ffffff;
  border-bottom: 1px solid #f0f0f0;
  font-family: PingFang SC, PingFang SC;
  font-weight: bold;
  font-size: 14px;
  color: #333333;
  line-height: 17px;
  text-align: left;
  font-style: normal;
  text-transform: none;
  padding: 12px 16px;
}
.section-card :deep(.ant-table-thead > tr > th:first-child) {
  text-align: left;
  padding-right: calc(16px - 20px);
  box-sizing: border-box;
}
.section-card :deep(.ant-table-thead > tr > th:not(:first-child)) {
  text-align: center;
}
.section-card :deep(.ant-table-tbody > tr > td) {
  border-bottom: 1px solid #f0f0f0;
  font-family: PingFang SC, PingFang SC;
  font-weight: 400;
  font-size: 14px;
  color: #333333;
  line-height: 17px;
  text-align: left;
  font-style: normal;
  text-transform: none;
  padding: 12px 16px;
}
.section-card :deep(.ant-table-tbody > tr > td:first-child) {
  text-align: left;
  padding-right: calc(16px - 20px);
  box-sizing: border-box;
}
.section-card :deep(.ant-table-tbody > tr > td:not(:first-child)) {
  text-align: center;
}
.section-card :deep(.ant-table-tbody > tr:hover > td) {
  background: #fafafa;
}
.project-summary-card :deep(.ant-card-body) {
  padding: 20px;
  background: #ffffff;
}
.project-summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  margin-top: 0;
}
.project-summary-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.project-summary-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.project-summary-title {
  font-family: PingFang SC, PingFang SC;
  font-weight: bold;
  font-size: 18px;
  color: #101010;
  text-align: left;
  font-style: normal;
  text-transform: none;
}
.time-filter-label {
  font-size: 14px;
  color: #666;
}
.time-filter-picker {
  min-width: 240px;
  border-radius: 4px;
}
.time-filter-picker :deep(.ant-picker) {
  border-radius: 4px;
}
.task-project-link {
  cursor: pointer;
}
.project-stat {
  display: flex;
  align-items: center;
  gap: 32px;
  position: relative;
}
.project-circle {
  position: relative;
  width: 140px;
  height: 140px;
  flex-shrink: 0;
}
.usage-chart {
  width: 140px;
  height: 140px;
}
.circle-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  line-height: 1.2;
}
.circle-text strong {
  font-size: 18px;
  color: #1f2533;
}
.circle-text span {
  font-size: 12px;
  color: #8d96a7;
}
.project-summary-list {
  margin-left: 50px;
  flex: 1;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: 12px;
}
.summary-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #f6f8fb;
  border-radius: 12px;
  padding: 18px 20px;
  min-height: 90px;
}
.summary-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: #e3e9f5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: #3c6df0;
}
.summary-info .label {
  font-family: PingFang SC, PingFang SC;
  font-weight: 400;
  font-size: 16px;
  color: #555555;
  text-align: left;
  font-style: normal;
  text-transform: none;
  margin: 0 0 0px 0;
}
.summary-info .value {
  font-family: Douyin Sans, Douyin Sans;
  font-weight: bold;
  font-size: 30px;
  color: #101010;
  text-align: left;
  font-style: normal;
  text-transform: none;
  margin: 0;
}
.detail-link {
  color: #666666;
  font-size: 14px;
}
.bottom-section .section-card {
  height: 360px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
@media (max-width: 1199px) {
  .bottom-section .section-card {
    height: auto;
    min-height: 300px;
  }
}
.bottom-section .section-card :deep(.ant-card-body) {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0 20px 20px 20px;
  margin-top: 0;
}
.bottom-section .section-card.step-card :deep(.ant-card-body) {
  padding: 20px 20px 20px 20px;
}
.bottom-section .scrollable {
  flex: 1;
  overflow-y: auto;
  padding-right: 4px;
}
.step-card__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  margin-top: 0;
  padding-top: 0;
  
  span {
    font-family: PingFang SC, PingFang SC;
    font-weight: bold;
    font-size: 18px;
    color: #101010;
    text-align: left;
    font-style: normal;
    text-transform: none;
  }
}
.step-card__header small,
.step-card__header .update-time {
  color: #8d96a7;
}
/* 今日步数排行：隐藏纵向滚动条，保留滚动 */
.step-card .step-rank-panel-body::-webkit-scrollbar {
  width: 0 !important;
  display: none;
}
.step-card .step-rank-panel-body {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
/* 今日步数排行：与 index.vue 一致，盾牌徽章 + 列表样式 */
.step-card .rank-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.step-card .rank-list li {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #f1f3f7;
}
.step-card .rank-num {
  width: 28px;
  height: 32px;
  clip-path: polygon(0 6px, 6px 0, calc(100% - 6px) 0, 100% 6px, 100% 85%, 50% 100%, 0 85%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  color: #fff;
  margin-right: 12px;
  flex-shrink: 0;
  padding-top: 2px;
}
.step-card .rank-num.rk-1 {
  background: #e74c3c;
}
.step-card .rank-num.rk-2 {
  background: #ff9f43;
}
.step-card .rank-num.rk-3 {
  background: #ffc53d;
}
.step-card .rank-num.rk-4,
.step-card .rank-num.rk-5 {
  background: #95a5a6;
}
.step-card .rank-list img {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  margin-right: 12px;
}
.step-card .rank-info {
  flex: 1;
}
.step-card .rank-info .name {
  margin: 0;
  font-weight: 600;
  color: #1f2430;
}
.step-card .rank-info .desc {
  margin: 0;
  color: #8d96a7;
  font-size: 12px;
}
.step-card .rank-step {
  font-weight: 600;
  color: #1f2430;
}
.step-card .panel-empty {
  padding: 16px 0;
}
.dashboard-image {
  width: 100%;
  display: block;
}
@media (min-width: 1920px) {
  .dashboard-container {
    padding: 0;
  }
  /* 大屏下项目数据模块四个卡片强制一行展示，允许适当压缩宽度 */
  .project-stat {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    gap: 24px;
  }
  .project-circle {
    flex-shrink: 0;
  }
  .project-summary-list {
    margin-left: 24px;
    display: flex;
    flex-wrap: nowrap;
    gap: 12px;
    width: 100%;
    overflow: hidden;
  }
  .project-summary-card .summary-card {
    box-sizing: border-box;
    flex: 0 0 calc((100% - 36px) / 4); /* 4 卡片 + 3 个 12px 间距 */
    max-width: calc((100% - 36px) / 4);
  }
}
@media (max-width: 1919px) and (min-width: 1680px) {
  .dashboard-container {
    padding: 0;
  }
  /* 1680-1919 宽度下同样保持项目数据四个卡片一行展示 */
  .project-stat {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    gap: 24px;
  }
  .project-circle {
    flex-shrink: 0;
  }
  .project-summary-list {
    margin-left: 24px;
    display: flex;
    flex-wrap: nowrap;
    gap: 12px;
    width: 100%;
    overflow: hidden;
  }
  .project-summary-card .summary-card {
    box-sizing: border-box;
    flex: 0 0 calc((100% - 36px) / 4);
    max-width: calc((100% - 36px) / 4);
  }
}
@media (max-width: 1679px) and (min-width: 1366px) {
  .dashboard-container {
    padding: 0;
  }
  .project-stat {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    gap: 24px;
  }
  .project-summary-list {
    margin-left: 24px;
    width: 100%;
    display: flex;
    flex-wrap: nowrap;
    gap: 12px;
    overflow: hidden;
  }
  .project-summary-card .summary-card {
    box-sizing: border-box;
    flex: 0 0 calc((100% - 36px) / 4);
    max-width: calc((100% - 36px) / 4);
  }
  .project-circle {
    width: 120px;
    height: 120px;
  }
  .usage-chart {
    width: 120px;
    height: 120px;
  }
  .summary-card {
    padding: 16px 18px;
    min-height: 80px;
  }
  .summary-icon {
    width: 44px;
    height: 44px;
    font-size: 20px;
  }
  .summary-info .value {
    font-size: 20px;
  }
  .bottom-section .section-card {
    height: 340px;
  }
}
@media (max-width: 1365px) and (min-width: 1200px) {
  .dashboard-container {
    padding: 0;
  }
  .project-stat {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    gap: 20px;
  }
  .project-summary-list {
    margin-left: 20px;
    width: 100%;
    display: flex;
    flex-wrap: nowrap;
    gap: 10px;
    overflow: hidden;
  }
  .project-summary-card .summary-card {
    box-sizing: border-box;
    flex: 0 0 calc((100% - 30px) / 4);
    max-width: calc((100% - 30px) / 4);
  }
  .project-circle {
    width: 110px;
    height: 110px;
  }
  .usage-chart {
    width: 110px;
    height: 110px;
  }
  .circle-text strong {
    font-size: 16px;
  }
  .summary-card {
    padding: 14px 16px;
    min-height: 75px;
  }
  .summary-icon {
    width: 40px;
    height: 40px;
    font-size: 18px;
  }
  .summary-info .value {
    font-family: Douyin Sans, Douyin Sans;
    font-weight: bold;
    font-size: 18px;
    color: #101010;
    text-align: left;
    font-style: normal;
    text-transform: none;
  }
  .summary-info .label {
    font-family: PingFang SC, PingFang SC;
    font-weight: 400;
    font-size: 12px;
    color: #555555;
    text-align: left;
    font-style: normal;
    text-transform: none;
  }
  .bottom-section .section-card {
    height: 320px;
  }
}
@media (max-width: 1199px) and (min-width: 992px) {
  .dashboard-container {
    padding: 0;
  }
  .project-stat {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;
    gap: 16px;
  }
  .project-summary-list {
    margin-left: 16px;
    width: 100%;
    display: flex;
    flex-wrap: nowrap;
    gap: 8px;
    overflow: hidden;
  }
  .project-summary-card .summary-card {
    box-sizing: border-box;
    flex: 0 0 calc((100% - 24px) / 4);
    max-width: calc((100% - 24px) / 4);
  }
  .project-circle {
    width: 100px;
    height: 100px;
  }
  .usage-chart {
    width: 100px;
    height: 100px;
  }
  .circle-text strong {
    font-size: 16px;
  }
  .circle-text span {
    font-size: 11px;
  }
  .summary-card {
    padding: 12px 14px;
    min-height: 70px;
  }
  .summary-icon {
    width: 36px;
    height: 36px;
    font-size: 16px;
  }
  .summary-info .value {
    font-family: Douyin Sans, Douyin Sans;
    font-weight: bold;
    font-size: 18px;
    color: #101010;
    text-align: left;
    font-style: normal;
    text-transform: none;
  }
  .summary-info .label {
    font-family: PingFang SC, PingFang SC;
    font-weight: 400;
    font-size: 12px;
    color: #555555;
    text-align: left;
    font-style: normal;
    text-transform: none;
  }
  .section-card :deep(.ant-card-head-title) {
    font-family: PingFang SC, PingFang SC;
    font-weight: bold;
    font-size: 18px;
    color: #101010;
    text-align: left;
    font-style: normal;
    text-transform: none;
  }
  .bottom-section .section-card {
    height: auto;
    min-height: 280px;
  }
}
@media (max-width: 991px) and (min-width: 768px) {
  .dashboard-container {
    padding: 0;
  }
  .project-stat {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  .project-summary-list {
    width: 100%;
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
  .project-circle {
    width: 90px;
    height: 90px;
  }
  .usage-chart {
    width: 90px;
    height: 90px;
  }
  .circle-text strong {
    font-size: 14px;
  }
  .circle-text span {
    font-size: 10px;
  }
  .summary-card {
    padding: 12px;
    min-height: 65px;
  }
  .summary-icon {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }
  .summary-info .value {
    font-family: Douyin Sans, Douyin Sans;
    font-weight: bold;
    font-size: 16px;
    color: #101010;
    text-align: left;
    font-style: normal;
    text-transform: none;
  }
  .summary-info .label {
    font-family: PingFang SC, PingFang SC;
    font-weight: 400;
    font-size: 11px;
    color: #555555;
    text-align: left;
    font-style: normal;
    text-transform: none;
  }
  .section-card {
    margin-bottom: 12px;
  }
  .section-card :deep(.ant-card-head-title) {
    font-family: PingFang SC, PingFang SC;
    font-weight: bold;
    font-size: 18px;
    color: #101010;
    text-align: left;
    font-style: normal;
    text-transform: none;
    padding: 10px 0;
  }
  .bottom-section .section-card {
    height: auto;
    min-height: 250px;
  }
  .step-card .rank-list li {
    padding: 10px 0;
  }
  .step-card .rank-info .name {
    font-size: 13px;
  }
  .step-card .rank-info .desc {
    font-size: 11px;
  }
}
@media (max-width: 767px) {
  .dashboard-container {
    padding: 0;
  }
  .project-summary-card :deep(.ant-card-body) {
    padding: 16px;
  }
  .project-stat {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  .project-summary-list {
    width: 100%;
    grid-template-columns: 1fr;
    gap: 10px;
  }
  .project-circle {
    width: 80px;
    height: 80px;
  }
  .usage-chart {
    width: 80px;
    height: 80px;
  }
  .circle-text strong {
    font-size: 14px;
  }
  .circle-text span {
    font-size: 10px;
  }
  .summary-card {
    padding: 12px;
    min-height: 60px;
  }
  .summary-icon {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }
  .summary-info .value {
    font-family: Douyin Sans, Douyin Sans;
    font-weight: bold;
    font-size: 16px;
    color: #101010;
    text-align: left;
    font-style: normal;
    text-transform: none;
  }
  .summary-info .label {
    font-family: PingFang SC, PingFang SC;
    font-weight: 400;
    font-size: 11px;
    color: #555555;
    text-align: left;
    font-style: normal;
    text-transform: none;
  }
  .section-card {
    margin-bottom: 12px;
  }
  .section-card :deep(.ant-card-head) {
    padding: 0 12px;
  }
  .section-card :deep(.ant-card-head-title) {
    font-family: PingFang SC, PingFang SC;
    font-weight: bold;
    font-size: 18px;
    color: #101010;
    text-align: left;
    font-style: normal;
    text-transform: none;
    padding: 10px 0;
  }
  .section-card :deep(.ant-card-body) {
    padding: 12px;
  }
  .bottom-section .section-card {
    height: auto;
    min-height: 250px;
  }
  .step-card__header {
    margin-bottom: 10px;
    
    span {
      font-family: PingFang SC, PingFang SC;
      font-weight: bold;
      font-size: 18px;
      color: #101010;
      text-align: left;
      font-style: normal;
      text-transform: none;
    }
  }
  .step-card__header small,
  .step-card__header .update-time {
    font-size: 11px;
  }
  .step-card .rank-list li {
    padding: 10px 0;
    flex-wrap: wrap;
  }
  .step-card .rank-info {
    flex: 1;
    min-width: 0;
  }
  .step-card .rank-info .name {
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .step-card .rank-info .desc {
    font-size: 11px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .step-card .rank-step {
    font-size: 13px;
  }
}

</style>
<style lang="less">
/* 弹窗挂载在 body，需全局样式才能生效：上内间距缩小 10px（约 24px → 14px） */
.ant-modal-body {
  padding-top: 14px !important;
}
/* 任务详情弹窗内「当前进度」下拉框圆角 4px（Modal 在 body，scoped 无效） */
.ant-modal-body .task-detail-status-select .ant-select-selector,
.ant-modal-body .task-detail-status-select .javaMain-select-selector,
.javaMain-modal-wrap .task-detail-status-select .ant-select-selector,
.javaMain-modal-wrap .task-detail-status-select .javaMain-select-selector {
  border-radius: 4px !important;
}
/* 公司看板：今日步数排行隐藏纵向滚动条 */
.dashboard-container .step-rank-panel-body::-webkit-scrollbar {
  width: 0 !important;
  display: none;
}
.dashboard-container .step-rank-panel-body {
  scrollbar-width: none;
  -ms-overflow-style: none;
}
</style>
